/* eslint-disable camelcase */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Checkbox, CircularProgress, Collapse, FormControl, Grid, IconButton, Input, InputAdornment, MenuItem, Select } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import moment from 'moment-timezone'

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { Helper } from '../../../common/helpers/Helper';
import { SubEntityCard } from '../../../common/components/SubEntity/SubEntityCard';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { Controller } from '../../../common/Controller';
import { AppButton } from '../../../common/components/AppButton';
import { AppHelper } from '../../../helpers/AppHelper';
import { CdpHelper } from '../../../helpers/CdpHelper';
import { StatCard } from '../../../common/components/StatCard';
import { TabTableCard } from '../../../common/components/tables/TabTableCard';
import { CRMHelper } from '../../../helpers/CRMHelper';
import { ButtonList } from '../../../common/components/ButtonList';
import WhatsappSessionPreview from '../../../common/components/WhatsappSessionPreview';
import WhatsappTemplatePreview from '../../../common/components/WhatsappTemplatePreview';
import {
  WhatsappUtils,
  normalizeWaPhoneForCompare,
  isWaContentSendable,
  getWaTemplateFromContent,
  buildWaSendPickerContentOptions,
  buildWaSendUnifiedMessageOptions,
  getWaContentIdFromIType,
  WA_CONTENT_TYPE_SESSION,
} from './WhatsappUtils';
import { buildPreviewComponentsFromTemplate } from './WhatsappPreviewAdapter';
import { PageDialog } from '../../../common/components/PageDialog';
import { StaffHelper } from '../../../common/helpers/StaffHelper';

const EDITABLE_TYPES = ['NOTE']
const VIEWABLE_TYPES = ['FORM_SUBMISSION', 'EMAIL']

function getWebhookPushResponse(current) {
  if (!current || current.iType !== 'EVENT') return null
  const ev = (current.payload && current.payload._eventSource) || {}
  if (ev.activityType !== 'WEBHOOK_PUSH') return null
  const webhookResponse = current.payload && current.payload.webhookResponse
  if (webhookResponse === undefined || webhookResponse === null) return null
  return webhookResponse
}

function getWaErrorMessage(error) {
  if (!error) return ''
  if (typeof error === 'string') return error.trim()
  const payload = error.payload || {}
  const errorDetail = payload.errorDetail || {}
  const message = (errorDetail.error_data && errorDetail.error_data.details) || errorDetail.message || payload.message || error.message
  return typeof message === 'string' ? message.trim() : ''
}

function getExtInteractionAgentEmail(current) {
  const email = current && current.extpayload && current.extpayload.agentEmail
  return typeof email === 'string' ? email.trim() : ''
}

function filterApprovedWaTemplates(list) {
  return (Array.isArray(list) ? list : []).filter(
    t => String((t && t.status) || '').toUpperCase() === 'APPROVED'
  )
}

function indexCachedWaTemplatesByNumber(data) {
  const byNumber = {}
  const items = data && data.items
  if (!items || Array.isArray(items) || typeof items !== 'object') return byNumber
  Object.keys(items).forEach((num) => {
    const approved = filterApprovedWaTemplates(items[num])
    byNumber[num] = approved
    const digits = normalizeWaPhoneForCompare(num)
    if (digits && !byNumber[digits]) byNumber[digits] = approved
  })
  return byNumber
}

function buildWaSendStep1FormDefinition({
  messageOptions,
  marketingKeys = [],
  waContentPreviewById,
}) {
  return {
    title: Helper.getString('sendQuickMessage'),
    formType: 'add',
    ctaButtonText: 'Send',
    maxWidth: 'md',
    preview: {
      component: 'wa_send_content_preview_adapter',
      title: 'Preview',
      sticky: false,
    },
    waContentPreviewById: waContentPreviewById || {},
    steps: [{
      fields: [
        {
          key: 'waMessageId',
          type: 'radio',
          label: 'Message',
          options: messageOptions || [],
          defaultValue: '',
          // eslint-disable-next-line camelcase
          v_required: { value: true, err: Helper.getString('provideWaMessage') },
          placement: 'left',
        },
        {
          key: 'waMarketingDeliveryWarning',
          type: 'html',
          htmlContent: `<p style="color:#eab308;font-size:16px;font-weight:500;line-height:1.4;margin:0">${Helper.getString('waMarketingDeliveryWarning')}</p>`,
          condition: { key: 'waMessageId', values: marketingKeys || [] },
          placement: 'left',
          // eslint-disable-next-line camelcase
          v_required: { value: false, err: '' },
        },
      ],
    }],
  }
}

function generateEmailStats(current) {
  const parts = []
  if(current) {
    const payload = current.payload || {}
    const openCount = current.emailOpenCount || payload.openCount || 0
    const clickCount = current.emailClickCount || payload.clickCount || 0
    const replyCount = current.emailReplyCount || payload.replyCount || 0
    const isBroadcastEmail = current.iType === 'BEMAIL'
    if (isBroadcastEmail || current.emailOpenTracking || openCount > 0) {
      parts.push(`Opens: ${openCount}`)
    }
    if (isBroadcastEmail || current.emailClickTracking || clickCount > 0) {
      parts.push(`Clicks: ${clickCount}`)
    }
    parts.push(`Replies: ${replyCount}`)
  }
  return parts.join(' | ')
}

function getEmailBounceCount(current) {
  return (current && (current.emailBounceCount || (current.payload && current.payload.bounceCount))) || 0
}

function getBroadcastEmailBody(current) {
  if (current.emailBodyHtmlText) {
    return Helper.addHtmlTextUrlQueryParams(current.emailBodyHtmlText, {__slxignore: true})
  }
  if (current.emailBodyPlainText) {
    return Helper.escapeHtml(current.emailBodyPlainText).replace(/\n/g, '<br/>')
  }
  return ''
}

function getBroadcastEmailSubject(current) {
  const emailRequest = (current.payload && current.payload.emailRequest) || {}
  return current.emailSubject || emailRequest.emailSubject || 'BROADCAST EMAIL'
}

function getAggrStats(stats, name, aggr = 'sum') {
  const key = name + '_' + aggr
  if (!stats || !stats[key] || !stats[key].value) { return 0 }
  return stats[key].value
}

function generateChartTabsData(orgId, hideWATab) {
  const isWAInteractionsViewEnabled = AppHelper.isWAInteractionsViewEnabled(orgId)
  const chartTabsData = {
    interactionsTable: [
      {
        title: Helper.getString('info'),
        key: 'summary',
      },
      {
        title: 'Interactions',
        key: 'interactions',
      }
    ]
  }

  if (!hideWATab && isWAInteractionsViewEnabled) {
    chartTabsData.interactionsTable.push({
      title: 'WhatsApp',
      key: 'whatsapp',
    })
  }

  return chartTabsData
}

function getTabIndexByKey(tabs, key) {
  const index = (tabs || []).findIndex(tab => tab && tab.key === key)
  return index >= 0 ? index : 0
}

function modifyFormDefForMobile(fieldDef) {
  if(fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields) {
    fieldDef.steps[0].fields.forEach((field)=>{
      if(field) {
        if(['radio','check_box','select'].includes(field.type)) {
          field.disableTyping = true
          field.disableClearable = true
        }
      }
    })
  }
}

function formatWAMessageText(text = '') {
  // Escape HTML to prevent XSS
  text = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  // Split text into URL and non-URL parts, process separately
  const parts = text.split(/(https?:\/\/[^\s<]+)/gi)

  text = parts.map((part, i) => {
    if (i % 2 === 1) {
      // This is a URL part - wrap in anchor, no formatting
      const href = part.replace(/&amp;/g, '&').replace(/&quot;/g, '"')
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${part}</a>`
    } else {
      // This is regular text - apply WhatsApp formatting
      return part
        .replace(/\*(.*?)\*/g, '<b>$1</b>')
        .replace(/_(.*?)_/g, '<i>$1</i>')
        .replace(/~(.*?)~/g, '<s>$1</s>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\r?\n/g, '<br>')
    }
  }).join('')

  return('<span>' + text + '</span>')
}

/** Formats form submission message for the interaction event stream: underscores → spaces, no newlines, linkify with label "Link", strip outer (). */
function formatEventStreamFormMessage(message, linkClassName) {
  if (message == null || message === '') {
    return ''
  }
  let s = String(message)
  s = s.replace(/\r?\n/g, ' ')
  s = s.replace(/_/g, ' ')
  s = s.trim()
  while (s.length >= 2 && s.charAt(0) === '(' && s.charAt(s.length - 1) === ')') {
    s = s.slice(1, -1).trim()
  }
  const urlRe = /(https?:\/\/[^\s<]+|www\.[^\s<]+|\b(?:[a-zA-Z0-9-]+\.)+(?:com|org|net|edu|gov|io|co|in|ai|app|dev)\b)/gi
  const nodes = []
  let lastIndex = 0
  let match
  let linkKey = 0
  while ((match = urlRe.exec(s)) !== null) {
    if (match.index > lastIndex) {
      const text = s.slice(lastIndex, match.index)
      if (text) {
        nodes.push(text)
      }
    }
    let href = match[0]
    if (!/^https?:\/\//i.test(href)) {
      href = 'http://' + href
    }
    nodes.push(
      <a
        key={`esfm-${linkKey++}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className={linkClassName}
      >
        Link
      </a>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < s.length) {
    const tail = s.slice(lastIndex)
    if (tail) {
      nodes.push(tail)
    }
  }
  if (nodes.length === 0) {
    return s
  }
  return (
    <>
      {nodes.map((node, i) => typeof node === 'string' ? (<React.Fragment key={`esfmt-${i}`}>{node}</React.Fragment>) : (node))}
    </>
  )
}

function formatFormSpecialEventData(data) {
  if (!data || typeof data !== 'object') return ''
  return Object.keys(data)
    .filter(k => {
      const v = data[k]
      return v !== undefined && v !== null && v !== '' && typeof v !== 'object'
    })
    .map(k => `${Helper.escapeHtml(k)}: ${Helper.escapeHtml(String(data[k]))}`)
    .join('<br/>')
}

function getSendWAMessageFieldDef(params) {
  const fieldDef = {
    title: 'Send WhatsApp Message',
    formType: 'add',
    ctaButtonText: 'Send',
    steps: [{
      fields: [
        {
          key: 'type',
          type: 'radio',
          label: 'Type',
          options: [{key: 'TEXT', label: 'TEXT'},{key: 'IMAGE', label: 'IMAGE'},{key: 'DOCUMENT', label: 'DOCUMENT'}],
          defaultValue: (params && params.defaultType) ? params.defaultType : 'TEXT',
          // eslint-disable-next-line camelcase
          v_required: { value: true, err: 'Provide type' },
          placement: 'left',
        },
        {
          key: 'messageBody',
          type: 'edit_text',
          label: 'Message',
          tip: 'Enter the message',
          v_required: { value: true, err: 'Provide message' },
          placement: 'left',
          multiline: true,
          condition: { key: 'type', value: 'TEXT', values: ['TEXT'] },
        },
        {
          key: 'attachment',
          linkName: 'attachmentsLink',
          type: 'document',
          subType: 'document',
          label: 'Attachment',
          tip: 'Select a file to attach to the message',
          // eslint-disable-next-line camelcase
          v_required: { value: true,  err: 'Provide attachment'  },
          placement: 'right',
          documentUpload: true,
          fileSizeLimit: 10485760,
          uploadAccept: 'application/pdf, text/csv',
          condition: { key: 'type', value: 'DOCUMENT', values: ['DOCUMENT'] },
          uploadMs: AppConfig.ENDPOINT
        },
        {
          key: 'image',
          linkName: 'imageLink',
          type: 'edit_text',
          label: 'Image (PNG/JPEG, max 1920px height)',
          imageHeight: 1920,
          mode: 'fit',
          tip: 'Upload an image in PNG or JPEG format',
          v_required: { value: true, err: 'Provide image'},
          v_min: {value: 1, err: 'Invalid image'},
          v_max: {value: 100, err: 'Invalid image'},
          imageUpload: true,
          uploadType: 'media',
          uploadAccept: 'image/png, image/jpeg, image/jpg',
          placement: 'right',
          condition: { key: 'type', value: 'IMAGE', values: ['IMAGE'] },
          uploadMs: AppConfig.ENDPOINT
        },
        {
          key: 'caption',
          type: 'edit_text',
          label: 'Caption',
          tip: 'Enter the caption',
          v_required: { value: false, err: 'Provide caption' },
          placement: 'left',
          multiline: true,
          condition: { key: 'type', value: 'IMAGE', values: ['IMAGE','DOCUMENT'] },
        },
      ]
    }],
  }
  return fieldDef
}

function extractFilename(path) {
  if (typeof path !== 'string') return ''

  const parts = path.split('/')

  if (parts.length < 3) return path

  return parts.slice(1, -1).join('/')
}

function notifyWAChatFocusChangeToNativeApp(currentUser, isActive = false) {
  if(currentUser) {
    const data = {
      type: 'WHATSAPP_CHAT_FOCUS_CHANGE',
      active: isActive,
      contactId: currentUser.id,
      userId: currentUser.userId,
      orgId: currentUser.orgId,
      userName: Helper.formatName(currentUser),
      subOrgId: currentUser.subOrgId,
    }
    AppHelper.postMessageToNativeApp(data)
  }
}

function isWithin24HoursWindow(waMessages,agentWABA) {
  let isWithin24Hours = false
  if(Array.isArray(waMessages) && waMessages.length > 0) {
    let incomingwaMessages = waMessages.filter((waMessage)=>['new','reply'].includes(waMessage.iType))
    if(agentWABA) {
      incomingwaMessages = incomingwaMessages.filter((waMessage) => waMessage.WABAPhone === agentWABA)
    }
    const latestIncomingMsg = incomingwaMessages &&  incomingwaMessages.length > 0 && incomingwaMessages[0]
    isWithin24Hours =  latestIncomingMsg && latestIncomingMsg.createdTs && moment().diff(moment(latestIncomingMsg.createdTs)) < 24 * 60 * 60 * 1000
  }
  return isWithin24Hours
}

function getOnlyWAInteractionsMessageKey(waMessages, agentWABA) {
  if(Array.isArray(waMessages) && waMessages.length > 0 && agentWABA) {
    const incomingwaMessages = waMessages.filter((waMessage)=>['new','reply'].includes(waMessage.iType))
    const latestIncomingMsg = incomingwaMessages && incomingwaMessages.length > 0 && incomingwaMessages[0]
    const hasRecentIncomingOnDifferentWABA = latestIncomingMsg && latestIncomingMsg.WABAPhone && latestIncomingMsg.WABAPhone !== agentWABA && latestIncomingMsg.createdTs && moment().diff(moment(latestIncomingMsg.createdTs)) < 24 * 60 * 60 * 1000
    if(hasRecentIncomingOnDifferentWABA) {
      return 'differentOutgoingNumberWAInteraction'
    }
  }
  return 'onlyWAInteractions'
}

function populateEmailFormDefFromSettings(fieldDefCp,fromInboxEmail,orgId) {
  function parseEmailMapping(input) {
    const result = {};

    if (!input || typeof input !== 'string') {
      return result;
    }

    const pairs = input.split('|');

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i].trim();
      if (!pair) continue;

      const colonIndex = pair.indexOf(':');
      if (colonIndex === -1) continue;

      const key = pair.slice(0, colonIndex).trim();
      const valuePart = pair.slice(colonIndex + 1).trim();

      if (!key || !valuePart) continue;

      const values = valuePart
        .split(',')
        .map(function (v) { return v.trim(); })
        .filter(function (v) { return v.length > 0; });

      if (values.length === 0) continue;

      if (!result[key]) {
        result[key] = [];
      }

      const existing = result[key];
      for (let j = 0; j < values.length; j++) {
        if (existing.indexOf(values[j]) === -1) {
          existing.push(values[j]);
        }
      }
    }

    return result;
  }

  function generateDefaultAddresses(emailField,crmInboxDefaultEmailMapping,fromInboxEmail) {
    let defaultValue = []
    if(emailField && crmInboxDefaultEmailMapping) {
      let defaultAddresses = emailField.defaultValue || []
      if(fromInboxEmail && Array.isArray(crmInboxDefaultEmailMapping[fromInboxEmail]) && crmInboxDefaultEmailMapping[fromInboxEmail].length > 0) {
        if(Array.isArray(defaultAddresses) && defaultAddresses.length > 0) {
          defaultAddresses = [...defaultAddresses, ...crmInboxDefaultEmailMapping[fromInboxEmail]]
        } else {
          defaultAddresses = crmInboxDefaultEmailMapping[fromInboxEmail]
        }
      }
      if(Array.isArray(crmInboxDefaultEmailMapping.all) && crmInboxDefaultEmailMapping.all.length > 0) {
        if(Array.isArray(defaultAddresses) && defaultAddresses.length > 0) {
          defaultAddresses = [...defaultAddresses, ...crmInboxDefaultEmailMapping.all]
        } else {
          defaultAddresses = crmInboxDefaultEmailMapping.all
        }
      }
      defaultValue = [...new Set(defaultAddresses)]
    }
    return defaultValue
  }

  const crmInboxDefaultEmailCCSettings = AppHelper.getSetting(orgId, 'CRM_INBOX_DEFAULT_EMAIL_CC')
  if(crmInboxDefaultEmailCCSettings) {
    const crmInboxDefaultEmailCCMapping = parseEmailMapping(crmInboxDefaultEmailCCSettings)
    const emailCCField = Helper.getField(fieldDefCp, 'emailCC')
    if(emailCCField && crmInboxDefaultEmailCCMapping) {
      const defaultValue = generateDefaultAddresses(emailCCField,crmInboxDefaultEmailCCMapping,fromInboxEmail)
      emailCCField.defaultValue = defaultValue
    }
  }

  const crmInboxDefaultEmailBCCSettings = AppHelper.getSetting(orgId, 'CRM_INBOX_DEFAULT_EMAIL_BCC')
  if(crmInboxDefaultEmailBCCSettings) {
    const crmInboxDefaultEmailBCCMapping = parseEmailMapping(crmInboxDefaultEmailBCCSettings)
    const emailBCCField = Helper.getField(fieldDefCp, 'emailBCC')
    if(emailBCCField && crmInboxDefaultEmailBCCMapping) {
      const defaultValue = generateDefaultAddresses(emailBCCField,crmInboxDefaultEmailBCCMapping,fromInboxEmail)
      emailBCCField.defaultValue = defaultValue
    }
  }
}
function getCallDialogFieldDef(crmUser, orgId, currentStaffUser) {
  const isClickToCallEnabled = AppHelper.isClickToCallEnabled(orgId)
  const isCarrierCallEnabled = AppHelper.isSettingEnabled(orgId, 'CRM_CONTACT_CARRIER_CALL')
  const isClickToZoomPhoneEnabled = AppHelper.isClickToZoomPhoneEnabled(orgId)
  const callOptions = []
  if (isClickToCallEnabled) {
    callOptions.push({
      label: 'Call Cloud',
      action: () => UiHelper.openModalForClickToCall.call(this, crmUser)
    })
  }
  if (isCarrierCallEnabled) {
    callOptions.push({
      key: 'carrier_call',
      label: 'Call Direct',
      action: () => {
        ApiHelper.trackContactStaffActivity(crmUser, 'CALL DIRECT')
        window.open(`tel:${crmUser.phone}`, '_blank')
      }
    })
  }
  if (isClickToZoomPhoneEnabled) {
    callOptions.push({
      key: 'zoom_phone',
      label: 'Call Zoom Phone',
      action: () => {
        ApiHelper.trackContactStaffActivity(crmUser, 'CALL ZOOM PHONE')
        window.open(`zoomphonecall://${crmUser.phone}`, '_blank')
      }
    })
  }
  return callOptions
}

function getMoreActionsDialogFieldDef(orgId) {
  const moreOptionsMobile = []
  moreOptionsMobile.push({
    key: 'refresh',
    label: 'Refresh',
    action: () => this.fetchItems(),
  });
  moreOptionsMobile.push({
    key: 'markAsUnread',
    label: 'Mark as Unread',
    action: () => this.markAsUnread()
  });
  return moreOptionsMobile;
}

export class CrmContact extends BaseEntityPageSlixta {

  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageKey = 'crm-contact'
    this.pageTitle = Helper.getString('crm-contact')
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.subOrgId = Controller.get().userMgr().getSubOrgId(this.orgId)
    this.teamsUnifiedView = AppHelper.isTeamsUnifiedView(this.orgId)
    this.crmUserId = this.props.match.params.userId
    this.staffUserId = Controller.get().getUserId()
    this.orgIdUserId = `${this.orgId}-${this.crmUserId}`
    this.attribsGroupId = 'settingscrmcontactattrs' + this.orgId
    this.inboxesGroupId = 'settingscrminboxes' + this.orgId
    this.creatableTypes = [{key:'NOTE', label:'NOTE'}]
    this.isMobileView = Helper.isMobileView()
    this.pageDialogMode = this.props.pageDialogMode || false
    this.isLogoForMobileHidden = this.props.isLogoForMobileHidden || false
    this.hideWATab = this.props.hideWATab || false
    this.isHeaderTitleHidden = this.props.isHeaderTitleHidden || false
    this.hideHeaderbar = this.props.hideHeaderbar || false
    this.isActiveInteractionsSelected = this.props.isActiveInteractionsSelected || false
    this.selectWATab =  this.props.selectWATab || false
    this.cachedData = this.props.cachedData || null

    this.waTemplatesByNumber = {}
    this.waTemplatesFetchPromise = null
    this._waTemplatesLoaded = false
    this._waChatPaintPending = false
    this.waContents = null
    this.waContentsFetchPromise = null
    this._waSendPickerPrefetchPromise = null
    this._waSendPickerReady = false
    this._openWaSendTemplateLoadId = 0

    this.state.statsActivity = []
    this.state.doc_count = 0
    this.state.rawWorkflows = []
    this.state.interactionStatsReady = false

    this.apiData = {
      orgId: this.orgId,
      userId: this.crmUserId,
    }
    this.staffUsers = []
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('crm-contacts'), to: `${this.baseRoute()}/crm-contacts` },
      { title: this.crmUserId },
    ]
    this.toFetch = this.buildFetchList()
    this.interactionsTableCellWidths = {
      Index: Styles.cellWidthSmall,
      Date: Styles.cellWidthNormal,
      Info: Styles.cellWidthNormal,
      Sender: Styles.cellWidthNormal,
      Interaction: this.isMobileView ? Styles.fullWidthWide : Styles.cellWidthWide,
      Message: Styles.fullWidthWide,
      Summary: Styles.fullWidthWide,
    }
    this.tableHeadersMap = {
      Index: (current) => { return current.__index + 1 },
      Date: (current) => { return Helper.formatTimestamp(current.createdTs) },
      Info: (current) => {
        return this.renderInteractionInfo(current)
      },
      Interaction: (current) => {
        return this.renderInteraction(current)
      },
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    const isContactEnrollEnabled = AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_ENROLL')
    const isContactEnrichEnabled = AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_ENRICH')
      || AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_ENRICH_PHONE')
    const isContactEditSummaryEnabled = AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_EDIT_SUMMARY')
    this.headerActions = [
      ...(isContactEditSummaryEnabled ? [{
        label: 'Edit',
        tooltip: 'Edit',
        icon: 'edit',
        type: 'icon-label',
        onClick: () => this.openModalForEditSummary(),
      }] : []),
      ...((this.subOrgId || this.isMobileView || !isContactEnrollEnabled) ? [] : [
        {
          label: 'Enroll',
          tooltip: 'Enroll',
          icon: 'dynamic_feed',
          type: 'icon-label',
          onClick: () => UiHelper.openModalForSequences.call(this, this.crmUser, this.crmSequences, this.crmInboxes),
        },
      ]),
      ...((this.subOrgId || this.isMobileView || !isContactEnrichEnabled) ? [] : [
        {
          label: 'Enrich',
          tooltip: 'Enrich',
          icon: 'dynamic_form',
          type: 'icon-label',
          onClick: () => UiHelper.openModalForEnrich.call(this, this.crmUser),
        },
      ]),
      {
        label: 'Interaction',
        tooltip: 'Add Interaction',
        icon: 'add',
        type: 'icon-label',
        onClick: () => this.openModalForAddStep1(),
      },
      {
        label: 'Unread',
        tooltip: 'Mark as Unread',
        icon: 'markunread',
        type: 'icon-label',
        onClick: () => this.handleMarkAsUnread(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      }
    ]

    this.uploadLocation = `${this.orgId}/__FILENAME__`
    this.uploadMs = AppConfig.CDP_MS.ENDPOINT

    this.state.chartTabsData = generateChartTabsData(this.orgId, this.hideWATab)
    this.state.selectedTabData = {
      interactionsTable: getTabIndexByKey(
        this.state.chartTabsData.interactionsTable,
        this.selectWATab ? 'whatsapp' : (this.isActiveInteractionsSelected ? 'interactions' : 'summary')
      ),
    }
    this.state.showUpdateInteractions = false
    this.state.showEventInteractions = false
    this.state.isTabTableLoading = !!this.selectWATab
    this.poller = null
    this.waPollingPaused = false
    this.state.isCallDialogOpen = false
    this.state.callDialogOptions = []
    this.state.isMoreActionsDialogOpen = false
    this.state.broadcastEmailPreviewOpen = false
    this.state.broadcastEmailPreviewHtml = ''
    this.state.broadcastEmailPreviewTitle = ''
    this.state.webhookResponseDialogOpen = false
    this.state.webhookResponseDialogJson = ''

    if (this.cachedData && this.cachedData.crmUser) {
      this.bindDialogContext()
      this.state.fetchState = ApiHelper.State.READY
      this.state.crmUser = this.crmUser
      this.state.staffUsers = this.staffUsers
      this.state.currentStaffUser = this.currentStaffUser
    }
  }

  getConfiguredInteractionStatKeys() {
    const setting = AppHelper.getSetting(this.orgId, 'CRM_INTERACTIONS_STATS')
    if (!setting || typeof setting !== 'string') return []
    return setting.split(',').map(k => k.trim()).filter(Boolean)
  }

  bumpInteractionStat(iType, statsActivity, docCount) {
    const configuredKeys = this.getConfiguredInteractionStatKeys()
    if (!iType || !configuredKeys.includes(iType)) {
      return { statsActivity, doc_count: docCount }
    }
    const nextStats = Array.isArray(statsActivity) ? statsActivity.map(s => ({ ...s })) : []
    const idx = nextStats.findIndex(s => s.key === iType)
    if (idx >= 0) {
      nextStats[idx] = { ...nextStats[idx], doc_count: (nextStats[idx].doc_count || 0) + 1 }
    } else {
      nextStats.push({ key: iType, doc_count: 1 })
    }
    return {
      statsActivity: nextStats,
      doc_count: (docCount || 0) + 1,
    }
  }

  mergeInteractionStats(apiStats, currentStats) {
    const configuredKeys = this.getConfiguredInteractionStatKeys()
    if (configuredKeys.length === 0) {
      return apiStats || []
    }
    const merged = Array.isArray(apiStats) ? apiStats.map(s => ({ ...s })) : []
    const current = Array.isArray(currentStats) ? currentStats : []
    configuredKeys.forEach(key => {
      const apiItem = merged.find(s => s.key === key)
      const currentItem = current.find(s => s.key === key)
      const apiCount = apiItem ? (apiItem.doc_count || 0) : 0
      const currentCount = currentItem ? (currentItem.doc_count || 0) : 0
      const count = Math.max(apiCount, currentCount)
      if (apiItem) {
        apiItem.doc_count = count
      } else if (currentCount > 0) {
        merged.push({ key, doc_count: count })
      }
    })
    return merged
  }

  bindDialogContext(results) {
    const cached = this.cachedData
    this.staffUsers = (cached && cached.usersOrg) ? cached.usersOrg : (results ? this.getResult(results, 'usersORG').items : [])
    this.staffUsers.forEach((staffUser)=>{
      staffUser.fullName = Helper.formatName(staffUser)
    })
    this.staffUsers = Helper.sortDictArray(this.staffUsers, 'fullName')
    this.crmUser = (cached && cached.crmUser) ? cached.crmUser : (results ? this.getResult(results, 'crmuser') : null)
    const crmUserFieldDefSrc = (cached && cached.crmUserFieldDef) ? cached.crmUserFieldDef : (results ? this.getResult(results, 'crmuser').fieldDef : null)
    this.crmUserFieldDef = crmUserFieldDefSrc ? JSON.parse(JSON.stringify(crmUserFieldDefSrc)) : null
    const accountFieldDefSrc = (cached && cached.accountFieldDef)
      ? cached.accountFieldDef
      : (results ? this.getResult(results, 'crmuser').accountFieldDef : null)
    this.accountFieldDef = (AppHelper.isABMEnabled(this.orgId) && accountFieldDefSrc)
      ? JSON.parse(JSON.stringify(accountFieldDefSrc))
      : null
    this.crmContactsStats = (cached && cached.crmContactsStats) ? cached.crmContactsStats : this.crmContactsStats
    const stateField = Helper.getField(this.crmUserFieldDef, 'state')
    if(stateField) {
      UiHelper.renameLeadStatusFieldOptions(stateField, this.orgId)
    }
    this.crmContactAttrs = (cached && cached.crmContactAttrs) ? cached.crmContactAttrs : (results ? this.getResult(results, 'crmcontactattrs') : null)
    this.crmInboxes = (cached && cached.crmInboxes) ? cached.crmInboxes : (results ? this.getResult(results, 'crminboxes').items : [])
    this.crmEmailTemplates = (cached && cached.crmEmailTemplates) ? cached.crmEmailTemplates.items : (results ? this.getResult(results, 'crmemailtemplates').items : [])
    this.crmEmailTemplateFieldDef = (cached && cached.crmEmailTemplates) ? cached.crmEmailTemplates.fieldDef : (results ? this.getResult(results, 'crmemailtemplates').fieldDef : null)
    this.crmUserSettings = (cached && cached.crmUserSettings) ? cached.crmUserSettings : (results ? this.getResult(results, 'crmusersettings') : null)
    this.crmSequences = (cached && cached.crmSequences) ? cached.crmSequences : (results ? CdpHelper.filterOutDeletedCampaigns(this.getResult(results, 'crmsequences').items).sort((a, b) => b.createdTs - a.createdTs) : [])
    this.crmAccountNames = (cached && cached.crmAccountNames) ? cached.crmAccountNames : (results ? this.getResult(results, 'crmaccountnames') : [])
    this.subOrgs = this.teamsUnifiedView
      ? AppHelper.getAccessibleTeams(this.orgId)
      : ((cached && cached.subOrgs) ? cached.subOrgs.items : (results && this.getResult(results, 'suborgs') ? this.getResult(results, 'suborgs').items : []))
    const showTenantNameInHeader = AppHelper.isSettingEnabled(this.orgId, 'TENANT_NAME_IN_HEADER')
    if (this.props.pageTitle) { this.pageTitle = this.props.pageTitle }
    else if (showTenantNameInHeader) {
      this.pageTitle = UiHelper.getOrgTitle() ||  this.pageTitle
    }
    this.crmUserSettingsEmailSignatureRichText = (this.crmUserSettings && this.crmUserSettings.emailSignatureRichText) || ''
    this.currentStaffUser = this.staffUsers.find(u => u.userId === this.staffUserId) || {}
    if (this.crmUserFieldDef && this.crmUserFieldDef.steps && this.crmUserFieldDef.steps[0]) {
      Helper.loadAttrOptions(this.crmUserFieldDef, this.crmContactAttrs, 'tags')
      const tagsField = Helper.getField(this.crmUserFieldDef, 'tags')
      if(tagsField && tagsField.options) {
        tagsField.options = tagsField.options.sort((a, b) => a.label.localeCompare(b.label));
      }
      UiHelper.populateOptions(this.crmUserFieldDef, {items: StaffHelper.uniqueStaffByUserId(this.staffUsers)}, 'assignee')
      UiHelper.populateOptions(this.crmUserFieldDef, {items: this.crmAccountNames}, 'accounts')
      UiHelper.populateOptions(this.crmUserFieldDef, {items: this.crmAccountNames}, 'primaryAccountId')
      UiHelper.populateOptions(this.crmUserFieldDef, {items: this.subOrgs}, 'subOrgId')
      if ((this.subOrgId && !this.teamsUnifiedView) || !AppHelper.isTeamsEnabled(this.orgId)) {
        this.crmUserFieldDef.steps[0].fields = this.crmUserFieldDef.steps[0].fields.filter(f => f.key !== 'subOrgId')
      }
      if(!AppHelper.isABMEnabled(this.orgId)) {
        this.crmUserFieldDef.steps[0].fields = this.crmUserFieldDef.steps[0].fields.filter((field)=>!['accounts','primaryAccountId'].includes(field.key))
      } else {
        const accountsField = Helper.getField(this.crmUserFieldDef, 'accounts')
        if (accountsField) {
          accountsField.options.forEach(o => o.href = this.baseRoute() + '/crm-accounts/' + o.key)
        }
        const primaryAccountIdField = Helper.getField(this.crmUserFieldDef, 'primaryAccountId')
        if (primaryAccountIdField) {
          primaryAccountIdField.options.forEach(o => o.href = this.baseRoute() + '/crm-accounts/' + o.key)
        }
      }
    }

    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('crm-contacts'), to: `${this.baseRoute()}/crm-contacts` },
      { title: Helper.formatName(this.crmUser) || '?' },
    ]

    if(AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_EDIT_INTERACTION') && this.creatableTypes.every(ct => ct.key !== 'EDIT_CONTACT')) {
      this.creatableTypes.unshift({key:'EDIT_CONTACT', label:'EDIT CONTACT'})
    }

    if(AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_EMAIL_INTERACTION') && this.creatableTypes.every(ct => ct.key !== 'EMAIL')) {
      this.creatableTypes.push({key:'EMAIL', label:'EMAIL'})
    }

    if(this.headerActions && this.crmUser && this.crmUser.phone) {
      if (!this.headerActions.some(action => action.label === 'Call') && AppHelper.isClickToCallEnabled(this.orgId)) {
        this.headerActions.splice(this.headerActions.length - 1, 0, {
          label: 'Call',
          tooltip: 'Call Cloud',
          icon: 'call',
          type: 'icon-label',
          onClick: () => UiHelper.openModalForClickToCall.call(this, this.crmUser),
        })
      }
      if (!this.headerActions.some(action => action.key === 'carrier_call') && AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_CARRIER_CALL')) {
        this.headerActions.splice(this.headerActions.length - 1, 0, {
          key: 'carrier_call',
          label: 'Call',
          tooltip: 'Call Direct',
          icon: 'phone_forwarded',
          type: 'icon-label',
          onClick: (e) => this.handleCarrierCallClick(e, this.crmUser)
        })
      }
      if (!this.headerActions.some(action => action.key === 'zoom_phone') && AppHelper.isClickToZoomPhoneEnabled(this.orgId)) {
        this.headerActions.splice(this.headerActions.length - 1, 0, {
          key: 'zoom_phone',
          label: 'Call',
          tooltip: 'Call Zoom Phone',
          icon: 'phone_in_talk',
          type: 'icon-label',
          onClick: (e) => this.handleZoomPhoneCallClick(e, this.crmUser)
        })
      }
      if (!this.headerActions.some(action => action.key === 'whatsapp') && (AppHelper.isClickToWhatsAppEnabled(this.orgId) || AppHelper.isClickToWhatsAppAppEnabled(this.orgId))) {
        this.headerActions.splice(this.headerActions.length - 1, 0, {
          key: 'whatsapp',
          label: 'WhatsApp',
          tooltip: 'Click to WhatsApp',
          icon: <IconButton disableRipple className={css(Styles.waicon)}><WhatsAppIcon fontSize='small'/></IconButton>,
          type: 'icon-label',
          onClick: async () => await UiHelper.openModalForClickToWhatsApp.call(this, this.crmUser),
        })
      }
    }

    let allowedImageFormats = null
    const sendWAMessageFieldDef = getSendWAMessageFieldDef()
    if(sendWAMessageFieldDef) {
      const imageField = Helper.getField(sendWAMessageFieldDef, 'image')
      if(imageField) {
        allowedImageFormats = Helper.getAllowedFormatsFromImageField(imageField)
      }
    }
    this.enableAssetPicker({allowedFormats: allowedImageFormats})
    this.state.waTextMsg = ''
  }

  kickoffInitialWaTabFetch() {
    if(this.selectWATab && !this._didInitialSelectWAFetch) {
      this._didInitialSelectWAFetch = true
      setTimeout(() => {
        notifyWAChatFocusChangeToNativeApp(this.crmUser, true)
        if(this.crmUser && this.crmUser.phone) {
          this.fetchWaMessages({isPolling: false, includeAgentWABA: true})
        } else {
          this.setState({ isTabTableLoading: false })
        }
      }, 150)
    }
  }

  componentDidMount() {
    this.updateDialogHeader()
    if (this.cachedData && this.cachedData.crmUser) {
      this.kickoffInitialWaTabFetch()
      this.fetchInteractions()
      this.clearCachedData()
    } else {
      this.fetchItems()
    }
    this.pageHideHandler = () => {
      notifyWAChatFocusChangeToNativeApp(this.crmUser, false)
    }
    window.addEventListener('pagehide', this.pageHideHandler)
  }

  updateDialogHeader() {
    const getClickHandler = (handler) => {
      return (...args) => {
        if (!this.crmInteractions || this.state.fetchState !== ApiHelper.State.READY || this.state.isTabTableLoading) {return;}
        return handler.apply(this, args)
      }
    }
    if (this.props.setDialogHeader) {
      let headerActionsForDisplay = this.headerActions
      if (this.isMobileView) {
        headerActionsForDisplay = this.headerActions.map(action => {
          if (!['Call', 'Refresh', 'Unread'].includes(action.label)) {
            return action
          }
          return null
        }).filter(Boolean)

        if (this.crmUser && this.crmUser.phone) {
          const callOptions = getCallDialogFieldDef.call(this, this.crmUser, this.orgId, this.currentStaffUser)
          if (callOptions.length > 0) {
            headerActionsForDisplay.push({
              label: 'Call',
              tooltip: 'Call Options',
              icon: 'call',
              type: 'icon-label',
              isMenu: true,
              onClick: () => {
                if (callOptions.length === 1) {
                  callOptions[0].action()
                } else {
                  this.openCallDialog(callOptions)
                }
              },
            })
          }
        }
        const moreOptionsMobile = getMoreActionsDialogFieldDef.call(this, this.orgId)
        headerActionsForDisplay.push({
          label: 'More',
          tooltip: 'More Actions',
          icon: 'more_vert',
          type: 'icon-label',
          isMenu: true,
          onClick: () => this.openMoreActionsDialog(moreOptionsMobile),
        })
      }
      this.props.setDialogHeader(
        <div className={css(Styles.dialogHeadercontainer)}>
          {headerActionsForDisplay && headerActionsForDisplay.map((actionItem, index) => {
            if (!actionItem) return null
            if (actionItem.type === 'icon') {
              return (
                <div className={css(Styles.link)} key={index}>
                  <Tooltip title={actionItem.tooltip} arrow classes={{ popper: css(Styles.tooltip) }}>
                    <div className={css(Styles.dialogHeaderImageContainer)} onClick={getClickHandler(actionItem.onClick)}>
                      <i className='material-icons-outlined' style={{ fontSize: 20, margin: 14, color: '#222222' }} >{actionItem.icon}</i>
                    </div>
                  </Tooltip>
                </div>
              )
            } else if (actionItem.type === 'icon-label') {
              return (
                <div className={css(Styles.link)} key={index}>
                  <Tooltip title={actionItem.tooltip} arrow classes={{ popper: css(Styles.tooltip) }}>
                    <div className={css(Styles.dialogHeaderIconLabelContainer)} onClick={getClickHandler(actionItem.onClick)}>
                      {typeof actionItem.icon === 'string' && <div><i className='material-icons-outlined' style={{ fontSize: 20 }} >{actionItem.icon}</i></div>}
                      {typeof actionItem.icon !== 'string' && actionItem.icon}
                      <p className={css(Styles.dialogHeaderIconLabelText)}>{actionItem.label}</p>
                    </div>
                  </Tooltip>
                </div>
              )
            } else if (actionItem.type === 'icon-label-link') {
              return (
                <div className={css(Styles.link)} key={index}>
                  <a
                    className={css(Styles.iconLabelLink)}
                    href={this.state.fetchState === ApiHelper.State.LOADING ? undefined : actionItem.link}
                    target='_blank'
                    onClick={(e) => { if (this.state.fetchState === ApiHelper.State.LOADING) { e.preventDefault() } }}>
                    <Tooltip title={actionItem.tooltip} arrow classes={{ popper: css(Styles.tooltip) }}>
                      <div className={css(Styles.dialogHeaderIconLabelContainer)}>
                        {typeof actionItem.icon === 'string' && <div><i className='material-icons-outlined' style={{ fontSize: 20 }} >{actionItem.icon}</i></div>}
                        {typeof actionItem.icon !== 'string' && actionItem.icon}
                        <p className={css(Styles.dialogHeaderIconLabelText)}>{actionItem.label}</p>
                      </div>
                    </Tooltip>
                  </a>
                </div>
              )
            } else {
              return (
                <AppButton
                  key={index}
                  buttonStyle={Styles.dialogHeaderButton}
                  onClick={getClickHandler(actionItem.onClick)}
                  buttonText={actionItem.label}
                  icon={actionItem.icon || 'add'}
                />
              )
            }
          })}
        </div>
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const hasCrmUser = this.crmUser && this.crmUser.phone
    const DEFAULT_POLLING_INTERVAL_MS = 15000
    let waPollingInterval = Number.parseInt(AppHelper.getSetting(this.orgId, 'WA_INTERACTIONS_POLLING_INTERVAL'))
    if (Number.isNaN(waPollingInterval)) { waPollingInterval = DEFAULT_POLLING_INTERVAL_MS }
    if (!this.poller && hasCrmUser && AppHelper.isWAInteractionsViewPollingEnabled(this.orgId)) {
      this.poller = setInterval(() => {
        if (this.waPollingPaused) { return }
        this.fetchWaMessages({isPolling: true, markAsRead: true, includeAgentWABA: true})
      }, waPollingInterval)
    }

    const isWATabActive = this.crmUser && this.crmUser.phone && this.isWATabSelected()

    if(!isWATabActive) {
      this.stopPolling()
    }

    if(prevState.fetchState !== this.state.fetchState || prevState.isTabTableLoading !== this.state.isTabTableLoading) {
      this.updateDialogHeader()
    }
  }

  componentWillUnmount() {
    notifyWAChatFocusChangeToNativeApp(this.crmUser, false)
    this.stopPolling()
    window.removeEventListener('pagehide', this.pageHideHandler)
  }

  stopPolling() {
    if (this.poller) {
      clearInterval(this.poller)
      this.poller = null
    }
  }

  pauseWaPolling() {
    this.waPollingPaused = true
  }

  resumeWaPolling() {
    this.waPollingPaused = false
  }

  fetchInteractions() {
    const nextState = {}
    if (this.state.fetchState !== ApiHelper.State.READY) {
      nextState.fetchState = ApiHelper.State.LOADING
      nextState.loadingMode = ApiHelper.LOADING_MODE.INSTANT_LOAD
    }
    if (this.getSelectedTabKey() === 'interactions') {
      nextState.isTabTableLoading = true
    }
    if (Object.keys(nextState).length > 0) {
      this.setState(nextState)
    }
    const includeStats = this.getConfiguredInteractionStatKeys().length > 0
    const reqPath = ['crminteractions']
    ApiHelper.callAwait({
      ms:  AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: reqPath,
      queryParams: {
        orgId: this.orgId,
        orgIdUserId: this.orgIdUserId,
        pb: 'fieldDef',
        limit: 100,
        _includeWorkflows: 'YES',
        ...AppHelper.getTeamApiParams(this.orgId, '_staffSubOrgId'),
        ...(includeStats ? { _includeStats: 'YES' } : {}),
      }
    }).then(result => {
      if(result) {
        this.crmInteractions = result.items
        this.fieldDef = result.fieldDef
        this.workflows = CRMHelper.extractWorkflows(result)

        UiHelper.populateOptions(this.fieldDef, {items: this.staffUsers}, 'creatorId')
        this.fieldDef.steps[0].fields.find(f => f.key === 'creatorId').options.push({key: this.orgIdUserId, label: Helper.formatName(this.crmUser)})
        UiHelper.populateOptions(this.fieldDef, {items: StaffHelper.uniqueStaffByUserId(this.staffUsers)}, 'noteMentions')
        UiHelper.attachTeamToFieldLabel(this.fieldDef,'noteMentions',this.subOrgs)
        const emailFromField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailFrom')
        const emailFromInboxField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailFromInbox')
        emailFromInboxField.v_required = {...emailFromInboxField.v_required, value: true}
        const eligibleSendInboxes = CRMHelper.filterInboxes(this.crmInboxes, 'send')
        const sendInboxOptions = CRMHelper.toInboxSelectOptions(eligibleSendInboxes)
        eligibleSendInboxes.forEach((inbox, i) => {
          const opt = sendInboxOptions[i]
          emailFromField.options.push({
            key: inbox.email,
            label: inbox.email,
            inboxId: inbox.id,
            inboxEmail: inbox.email,
            healthColor: opt.healthColor,
            healthTooltip: opt.healthTooltip,
          })
        })
        emailFromInboxField.options.push(...sendInboxOptions)
        if (eligibleSendInboxes.length > 0) {
          emailFromField.defaultValue = eligibleSendInboxes[0].email
          emailFromInboxField.defaultValue = eligibleSendInboxes[0].id
        }
        const addresseeFieldNames = ['emailTo', 'emailCC', 'emailBCC']
        let addresseeOptions = this.staffUsers.map(staffUser => {
          const fullEmail = Helper.formatUserEmail(staffUser)
          if (staffUser.email) {
            return {key: staffUser.email, label: fullEmail}
          }
        }).filter(a => !!a)

        addresseeOptions = [...new Map(addresseeOptions.map(item => [item.key, item])).values()]
        addresseeFieldNames.forEach(fieldName => {
          const field = this.fieldDef.steps[0].fields.find(f => f.key === fieldName)
          if (field) field.options = [...addresseeOptions]
          if (this.crmUser.email) {
            if(!(addresseeOptions && addresseeOptions.some(option => option.key === this.crmUser.email))) {
              const fullEmail = Helper.formatUserEmail(this.crmUser)
              field.options.unshift({key: this.crmUser.email, label: fullEmail})
            }
            if (fieldName === 'emailTo') {
              field.defaultValue = [this.crmUser.email]
            }
          }
        })

        Object.values(this.workflows || {}).forEach(workflow => {
          if (this.creatableTypes.every(ct => ct.key !== workflow.key)) {
            this.creatableTypes.push({key: workflow.key, label: workflow.title})
          }
        })

        this.transform(result, this.crmUser);
        this.handleNextpage(result)
        const mergedItems = result.items
        const emailMessageIdOptions = []
        for (const i of mergedItems) {
          if (i.iType === 'EMAIL') {
            emailMessageIdOptions.push({
              key: i.emailMessageId,
              text: i.emailSubject,
            })
          }
        }
        const emailInReplyToMessageIdField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailInReplyToMessageId')
        if (emailInReplyToMessageIdField) emailInReplyToMessageIdField.options = emailMessageIdOptions
        this.setState({
          items: mergedItems,
          fetchState: ApiHelper.State.READY,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          ...(this._waChatPaintPending ? {} : { isTabTableLoading: false }),
          statsActivity: this.mergeInteractionStats(result.statsActivity || [], this.state.statsActivity),
          doc_count: Math.max(result.doc_count || 0, this.state.doc_count || 0),
          rawWorkflows: result._workflows || [],
          interactionStatsReady: true,
        }, () => this.updateDialogHeader())
      }
    }).catch(err => {
      this.setState({
        items: [],
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
        ...(this._waChatPaintPending ? {} : { isTabTableLoading: false }),
        interactionStatsReady: true,
      }, () => this.updateDialogHeader())
    })
  }

  fetchWaMessages({isPolling = false, markAsRead = false, includeAgentWABA = false, skipLoading = false}, callback) {
    if (!skipLoading) {
      if (!isPolling) this._waChatPaintPending = true
      this.setState({
        fetchState: ApiHelper.State.LOADING,
        loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
        ...(isPolling && this._waChatPaintPending ? {} : { isTabTableLoading: !isPolling }),
      })
    }
    const reqPath = ['miscs', 'get-wa-messages']
    return ApiHelper.callAwait({
      ms:  this.ms, method: 'GET', paths: reqPath,
      queryParams: {
        orgId: this.orgId,
        subOrgId: this.crmUser.subOrgId,
        contactId: this.crmUser.id,
        phone: this.crmUser.phone,
        includeMedia: 'YES',
        includeAgentWABA: includeAgentWABA ? 'YES' : 'NO',
        markAsRead,
      }
    }).then(async (results) => {
      const messages = (results && results.items) ? results.items : []
      const agentWABA = (results && results.agentWABANumber) ? results.agentWABANumber : null
      if(!(!results || results.message === Helper.getString('tooManyRequests'))) {
        if (isPolling && this._waChatPaintPending) {
          if (callback) callback(messages)
          return
        }
        const hasTemplateMessages = Array.isArray(messages) && messages.some(m => m && m.obPayload && m.obPayload.type === 'template')
        if (!isPolling && hasTemplateMessages) {
          await this.ensureWaTemplatesLoaded()
        } else if (hasTemplateMessages) {
          this.ensureWaTemplatesLoaded()
        }
        if (!isPolling && CRMHelper.isWaQuickMessageEnabled(this.orgId)) {
          this._waSendPickerReady = false
          this._waSendPickerPrefetchPromise = this.prefetchWaSendPickerData(agentWABA)
            .catch(() => {})
            .then(() => {
              this._waSendPickerReady = true
              this._waSendPickerPrefetchPromise = null
            })
        }
        if (!skipLoading) this._waChatPaintPending = false
        this.setState(skipLoading ? {
          waMessages: messages,
          agentWABA: agentWABA,
        } : {
          waMessages: messages,
          agentWABA: agentWABA,
          fetchState: ApiHelper.State.READY,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          isTabTableLoading: false,
        }, () => {
          if (callback) callback(messages)
        })
      } else if (callback) {
        callback(messages)
      }
    }).catch(err => {
      if (!skipLoading) this._waChatPaintPending = false
      this.setState(skipLoading ? {
        waMessages: [],
      } : {
        waMessages: [],
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
        isTabTableLoading: false
      }, () => {
        if (callback) callback([]);
      })
    })
  }

  ensureWaTemplatesLoaded = async () => {
    if (this._waTemplatesLoaded) return this.waTemplatesByNumber
    if (this.waTemplatesFetchPromise) return this.waTemplatesFetchPromise
    this.waTemplatesFetchPromise = ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['miscs', 'get-cached-wa-templates'],
      queryParams: { orgId: this.orgId },
    }).then((data) => {
      this.waTemplatesByNumber = indexCachedWaTemplatesByNumber(data)
      this._waTemplatesLoaded = true
      this.waTemplatesFetchPromise = null
      this.forceUpdate()
      return this.waTemplatesByNumber
    }).catch(() => {
      this.waTemplatesByNumber = this.waTemplatesByNumber || {}
      this._waTemplatesLoaded = true
      this.waTemplatesFetchPromise = null
      this.forceUpdate()
      return this.waTemplatesByNumber
    })
    return this.waTemplatesFetchPromise
  }

  prefetchWaSendPickerData = async () => {
    try {
      await this.ensureWaContentsLoaded()
    } catch (e) {
      this.waContents = []
    }
  }

  ensureWaTemplatesForNumber = () => {
    this.ensureWaTemplatesLoaded()
  }

  findCachedWaTemplateByName = (fromNumber, name) => {
    if (!name) return null
    const map = this.waTemplatesByNumber || {}
    const list = map[fromNumber] || map[normalizeWaPhoneForCompare(fromNumber)]
    if (!Array.isArray(list)) return null
    return list.find(t => t && t.name === name) || null
  }

  getTemplatePreviewComponentsForMessage = (current) => {
    const template = current && current.obPayload && current.obPayload.template
    if (!template || !template.name) return []
    const match = this.findCachedWaTemplateByName(current.WABAPhone, template.name)
    if (!match) return []
    let structure = match.components
    if (!Array.isArray(structure)) {
      structure = structure && typeof structure === 'object' ? Helper.dictToArray(structure) : []
    }
    if (structure.length === 0) return []
    const variableValues = WhatsappUtils.obPayloadTemplateToVariableValues(template)
    return buildPreviewComponentsFromTemplate(structure, variableValues, { hideTemplateExamplesWhenUnset: true })
  }

  handleCarrierCallClick = (e, current) => {
    e.preventDefault()
    if(current && current.phone) {
      try {
        ApiHelper.trackContactStaffActivity(current, 'CALL DIRECT')
        window.open(`tel:${current.phone}`, '_blank')
      } catch (err) {
        console.log(err)
      }
    }
    return false
  }

  handleZoomPhoneCallClick = (e, current) => {
    e.preventDefault()
    if (current && current.phone) {
      try {
        ApiHelper.trackContactStaffActivity(current, 'CALL ZOOM PHONE')
        window.open(`zoomphonecall://${current.phone}`, '_blank')
      } catch (err) {
        console.log(err)
      }
    }
    return false
  }

  async onFetchSuccess(results, options) {
    const { fetchNext } = options || {}
    if (!fetchNext) {
      if(!this.cachedData) {
        this.crmInteractions = this.getResult(results, 'crminteractions').items
        this.fieldDef = this.getResult(results, 'crminteractions').fieldDef
        this.workflows = CRMHelper.extractWorkflows(this.getResult(results, 'crminteractions'))
      }
      this.bindDialogContext(results)

      if(!this.cachedData) {
        UiHelper.populateOptions(this.fieldDef, {items: this.staffUsers}, 'creatorId')
        this.fieldDef.steps[0].fields.find(f => f.key === 'creatorId').options.push({key: this.orgIdUserId, label: Helper.formatName(this.crmUser)})
        UiHelper.populateOptions(this.fieldDef, {items: StaffHelper.uniqueStaffByUserId(this.staffUsers)}, 'noteMentions')
        UiHelper.attachTeamToFieldLabel(this.fieldDef,'noteMentions',this.subOrgs)
        const emailFromField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailFrom')
        const emailFromInboxField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailFromInbox')
        emailFromInboxField.v_required = {...emailFromInboxField.v_required, value: true}
        const eligibleSendInboxes = CRMHelper.filterInboxes(this.crmInboxes, 'send')
        const sendInboxOptions = CRMHelper.toInboxSelectOptions(eligibleSendInboxes)
        eligibleSendInboxes.forEach((inbox, i) => {
          const opt = sendInboxOptions[i]
          emailFromField.options.push({
            key: inbox.email,
            label: inbox.email,
            inboxId: inbox.id,
            inboxEmail: inbox.email,
            healthColor: opt.healthColor,
            healthTooltip: opt.healthTooltip,
          })
        })
        emailFromInboxField.options.push(...sendInboxOptions)
        if (eligibleSendInboxes.length > 0) {
          emailFromField.defaultValue = eligibleSendInboxes[0].email
          emailFromInboxField.defaultValue = eligibleSendInboxes[0].id
        }
        const addresseeFieldNames = ['emailTo', 'emailCC', 'emailBCC']
        let addresseeOptions = this.staffUsers.map(staffUser => {
          const fullEmail = Helper.formatUserEmail(staffUser)
          if (staffUser.email) {
            return {key: staffUser.email, label: fullEmail}
          }
        }).filter(a => !!a)

        addresseeOptions = [...new Map(addresseeOptions.map(item => [item.key, item])).values()]
        addresseeFieldNames.forEach(fieldName => {
          const field = this.fieldDef.steps[0].fields.find(f => f.key === fieldName)
          if (field) field.options = [...addresseeOptions]
          if (this.crmUser.email) {
            if(!(addresseeOptions && addresseeOptions.some(option => option.key === this.crmUser.email))) {
              const fullEmail = Helper.formatUserEmail(this.crmUser)
              field.options.unshift({key: this.crmUser.email, label: fullEmail})
            }
            if (fieldName === 'emailTo') {
              field.defaultValue = [this.crmUser.email]
            }
          }
        })
      }

      if(!this.cachedData) {
        Object.values(this.workflows || {}).forEach(workflow => {
          if (this.creatableTypes.every(ct => ct.key !== workflow.key)) {
            this.creatableTypes.push({key: workflow.key, label: workflow.title})
          }
        })
      }

      this.kickoffInitialWaTabFetch()

      if(this._refetchWaWithPageLoad) {
        this._refetchWaWithPageLoad = false
        if(this.crmUser && this.crmUser.phone) {
          await this.fetchWaMessages({ isPolling: false, includeAgentWABA: true, skipLoading: true })
        }
      }

    } else {
      this.state.items.pop() // remove manually added CONTACT_CREATED interaction
    }

    if(!this.cachedData) {
      this.transform(results[0], this.crmUser);
      this.handleNextpage(results[0])
      const mergedItems = fetchNext ? this.state.items.concat(results[0].items) : results[0].items
      const emailMessageIdOptions = []
      for (const i of mergedItems) {
        if (i.iType === 'EMAIL') {
          emailMessageIdOptions.push({
            key: i.emailMessageId,
            text: i.emailSubject,
          })
        }
      }
      const emailInReplyToMessageIdField = this.fieldDef.steps[0].fields.find(f => f.key === 'emailInReplyToMessageId')
      if (emailInReplyToMessageIdField) emailInReplyToMessageIdField.options = emailMessageIdOptions
      const interactionsResult = results[0]
      this.setState({
        items: mergedItems,
        ...(!fetchNext ? {
          statsActivity: this.mergeInteractionStats(interactionsResult.statsActivity || [], this.state.statsActivity),
          doc_count: Math.max(interactionsResult.doc_count || 0, this.state.doc_count || 0),
          rawWorkflows: interactionsResult._workflows || [],
          interactionStatsReady: true,
        } : {}),
      })
    }
    this.setState({
      staffUsers: this.staffUsers,
      currentStaffUser: this.currentStaffUser,
      crmUser: this.crmUser,
    })

    if(this.cachedData) {
      this.fetchInteractions()
    }

    if(this.crmUser && this.crmUser.phone) {
      this.updateDialogHeader()
    }
    this.clearCachedData()
  }

  clearCachedData() {
    this.cachedData = null
    this.toFetch = this.buildFetchList()
  }

  buildFetchList(forceFetchAll = false) {
    const useCache = this.cachedData && !forceFetchAll
    const includeStats = this.getConfiguredInteractionStatKeys().length > 0

    const fetchList = [
      ...((useCache) ? [] : [{ key: 'crminteractions', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crminteractions'], queryParams: {orgId: this.orgId, orgIdUserId: this.orgIdUserId, pb: 'fieldDef', limit: 100, _includeWorkflows: 'YES', ...AppHelper.getTeamApiParams(this.orgId, '_staffSubOrgId'), ...(includeStats ? { _includeStats: 'YES' } : {}) } }]),
      ...((useCache && this.cachedData.usersOrg) ? [] : [{ key: 'usersORG', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-org-users2'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } }]),
      ...((useCache && this.cachedData.crmUser && this.cachedData.crmUserFieldDef) ? [] : [{ key: 'crmuser', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmcontacts', this.orgIdUserId], queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', ...(AppHelper.isABMEnabled(this.orgId) ? { _enrichWithAccountFields: true } : {}) } }]),
      ...((useCache && this.cachedData.crmContactAttrs) ? [] : [{ key: 'crmcontactattrs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['items', `${this.attribsGroupId}-all`], queryParams: { orgId: this.orgId, groupId: this.attribsGroupId } }]),
      ...((useCache && this.cachedData.crmInboxes) ? [] : [{ key: 'crminboxes', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crminboxs'], queryParams: { orgId: this.orgId, orgIdUserId: `${this.orgId}-${this.staffUserId}`, includeShared: 'true' } }]),
      ...((useCache && this.cachedData.crmEmailTemplates) ? [] : [{ key: 'crmemailtemplates', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmemailtemplates'], queryParams: { orgId: this.orgId, pb: 'fieldDef', includeShared: 'true', limit: 200 } }]),
      ...((useCache && this.cachedData.crmUserSettings) ? [] : [{ key: 'crmusersettings', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['items', `settingscrmusersettings${this.orgId}-${this.staffUserId}-all`], queryParams: { orgId: this.orgId, groupId: `settingscrmusersettings${this.orgId}-${this.staffUserId}` } }]),
      ...((useCache && this.cachedData.crmSequences) ? [] : [{ key: 'crmsequences', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['campaigns'], queryParams: { orgId: this.orgId, iTypeState: 'DRIP-LIVE' } }]),
      ...((useCache && this.cachedData.crmAccountNames) ? [] : [{ key: 'crmaccountnames', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-crm-account-titles'], queryParams: { orgId: this.orgId } }])
    ]

    if (AppHelper.isTeamsEnabled(this.orgId) && !this.subOrgId && !(useCache && this.cachedData.subOrgs)) {
      fetchList.push({ key: 'suborgs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-sub-orgs'], queryParams: {orgId: this.orgId, parentOrgId: this.orgId } })
    }

    return fetchList
  }

   handleActionsSelectChange = async (event) => {
     await this.updateLinkedinConnectionStatus(event.target.value)
   }

   updateLinkedinConnectionStatus = async (linkedinConnectionStatus) => {
     this.setState({
       fetchState: ApiHelper.State.LOADING,
       loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
       inProcess: true
     })

     const apiEndPoint = ApiHelper.makeUrlPath2({
       ms: AppConfig.CDP_MS.ENDPOINT,
       paths: ['miscs', 'update-crm-contact'],
       queryParams: { id: this.crmUser.id, orgId: this.orgId, ...AppHelper.getTeamApiParams(this.orgId, 'staffSubOrgId') }
     })

     const apiPayload = {
       linkedinConnectionStatus
     }

     try {
       await ApiHelper.callAwait({
         method: 'POST',
         endPoint: apiEndPoint,
         jsonBody: apiPayload
       })

       const onReadyCallback = ()=>{
         this.setState({inProcess: false})
       }
       this.fetchItems(onReadyCallback)
     } catch (err) {
       this.setState({
         fetchState: ApiHelper.State.READY,
         loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
         inProcess: false,
         showToast: true,
         errMsg: Helper.getString('customerCareErr')
       })
     }
   }

  handleMarkAsUnread = async (e) =>
  {
    const isDialogMode = this.pageDialogMode
    this.setState({
      fetchState: ApiHelper.State.LOADING,
      loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
      inProcess: true
    })
    if (isDialogMode && this.props.onMarkAsUnreadStart) {
      this.props.onMarkAsUnreadStart()
    }
    const apiEndPoint = ApiHelper.makeUrlPath2({
      ms: AppConfig.CDP_MS.ENDPOINT,
      paths: ['miscs', 'update-crm-contact'],
      queryParams: { id: this.crmUser.id, orgId: this.orgId, ...AppHelper.getTeamApiParams(this.orgId, 'staffSubOrgId') }
    })

    const apiPayload = {
      orgId: this.orgId,
      lastIntSentStatus: 'UNREAD',
      lastActiveIntSentStatus: 'UNREAD'
    }

    try {
      const response = await ApiHelper.callAwait({
        method: 'POST',
        endPoint: apiEndPoint,
        jsonBody: apiPayload
      })

      if (isDialogMode && this.props.onMarkAsUnreadSuccess) {
        this.props.onMarkAsUnreadSuccess()
        return
      }
      this.setState({
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
        inProcess: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: 'Marked as Unread'
      })
    } catch (err) {
      if (isDialogMode && this.props.onMarkAsUnreadError) {
        this.props.onMarkAsUnreadError()
      }
      this.setState({
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
        inProcess: false,
        showToast: true,
        isErrorToast: true,
        toastMsg: Helper.getString('customerCareErr')
      })
    }
  }

    displayFieldActions = (item, fields) => {
      if(item && item.id === 'linkedinProfileUrl' && item.value) {
        const connectionStatusField = fields.filter(field => field.id === 'linkedinConnectionStatus')[0]
        return(
          <div className={css(Styles.fieldActionsContainer)}>
            {(!this.state.crmUser.linkedinConnectionStatus || this.state.crmUser.linkedinConnectionStatus === 'notrequested') && (<a href={Helper.toExternalHref(item.value)} target='_blank' rel='noopener noreferrer' className={css(Styles.link)} onClick={() => this.updateLinkedinConnectionStatus('connected')}>
              <AppButton
                buttonText={'Connect'}
                buttonStyle={Styles.connectButton}
                buttonTextStyle={Styles.connectButtonText}
                progressIconSize={16}
              />
            </a>)}
            {!this.state.inProcess && connectionStatusField && connectionStatusField.options && (<Select
              value={this.state.crmUser.linkedinConnectionStatus || 'notrequested'}
              onChange={(event)=>this.handleActionsSelectChange(event, item)}
              disableUnderline
              MenuProps={{
                disablePortal: true,
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
                classes: { paper: css(Styles.actionsSelectMenu) },
              }}
              classes={{ select: css(Styles.actionsSelection) }}
              className={css(Styles.actionsSelect)}
            >
              {connectionStatusField.options.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>)}
            {this.state.inProcess && <span className={css(Styles.actionCircularProgress)}>{UiHelper.itemLoadingView(25)}</span> }
          </div>
        )
      }
      return null
    }

    scrollHandler() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if(this.isWAView) {
            const container = document.querySelector('.MuiDialog-paper')
            container.scrollTop = container.scrollHeight
          } else if(this.isWATabSelected()) {
            window.scrollTo(0, document.body.scrollHeight)
          }
        });
      });
    }

    renderWAUnavailableError() {
      return([<p className={css(Styles.whatsAppUnavailableWarning)}>{Helper.getString('whatsAppUnavailableForContact')}</p>])
    }

    getInteractionTs = (item) => {
      if (!item) return 0
      return item.emailDateTs || item.createdTs || 0
    }

    getTableData(tableKey) {
      let tableData = null

      if(tableKey === 'interactions') {
        let interactions = []
        const sortedItems = (this.state && Array.isArray(this.state.items))
          ? [...this.state.items].sort((a, b) => this.getInteractionTs(b) - this.getInteractionTs(a))
          : []
        if(this.isMobileView) {
          if(sortedItems && sortedItems.length > 0) {
            let rowIndex = 0
            interactions = sortedItems.map((item,index)=>{
              if (item.iType === 'UPDATE' && !this.state.showUpdateInteractions) {
                return null
              }
              if (item.iType === 'EVENT' && !this.state.showEventInteractions) {
                return null
              }
              rowIndex = rowIndex + 1
              const interaction = this.renderInteractionMobile(item)
              return([interaction, item])
            }).filter(a => !!a)
          }

          tableData = {
            headers: ['Interaction', 'Data'],
            rows: interactions,
            textColumnIndices: [0],
            noWrapColumnIndices: [0],
            hiddenColumnIndices: [1]
          }
        } else {
          if(sortedItems && sortedItems.length > 0) {
            let rowIndex = 0
            interactions = sortedItems.map((item,index)=>{
              if (item.iType === 'UPDATE' && !this.state.showUpdateInteractions) {
                return null
              }
              if (item.iType === 'EVENT' && !this.state.showEventInteractions) {
                return null
              }
              rowIndex = rowIndex + 1
              const date = Helper.formatTimestamp(this.getInteractionTs(item))
              const info = this.renderInteractionInfo(item)
              const interaction = this.renderInteraction(item)
              return([rowIndex, date, info, interaction, item])
            }).filter(a => !!a)
          }

          tableData = {
            headers: ['Index', 'Date', 'Info', 'Interaction', 'Data'],
            rows: interactions,
            textColumnIndices: [0,1,2,3],
            noWrapColumnIndices: [2,3],
            hiddenColumnIndices: [4]
          }
        }
      } else if(tableKey === 'whatsapp') {
        let waMessages = []

        const crmUserCp = this.state.crmUser && JSON.parse(JSON.stringify(this.state.crmUser))

        if(!crmUserCp || !crmUserCp.phone) {
          waMessages = [this.renderWAUnavailableError()]
        } else if (!this.state.isTabTableLoading) {
          if(this.state && this.state.waMessages) {
            waMessages = this.state.waMessages
              .filter(item => item && (item.parsed || item.obPayload))
              .map((item) => [this.renderWhatsAppMessage(item)])
          }

          if (Array.isArray(this.state.waMessages)) {
            waMessages.unshift([this.renderSendWAMessageRow()])
          }
        }

        tableData = {
          headers: ['Message'],
          rows: waMessages,
          textColumnIndices: [0],
          noWrapColumnIndices: [0],
        }
      } else if(tableKey === 'summary') {
        tableData = {
          headers: ['Summary'],
          rows: [[this.renderSummaryCard()]],
          textColumnIndices: [0],
          noWrapColumnIndices: [0],
        }
      }
      return tableData
    }


  getWhatsAppMessageStatusTs = (item) => {
    if (item.sentTs) return item.sentTs
    if (item.failedTs) return item.failedTs
    return item.createdTs || item.ts || null
  }

  renderWhatsAppStatusIcon = (item) => {
    const format = (ts) => (ts ? Helper.formatTimestamp(ts) : '-')
    const STATUS_PRIORITY = [
      { key: 'createdTs', icon: 'schedule', color: 'grey' },
      { key: 'failedTs', icon: 'error_outline', color: '#EA0038' },
      { key: 'sentTs', icon: 'done', color: 'grey' },
      { key: 'deliveredTs', icon: 'done_all', color: 'grey' },
      { key: 'readTs', icon: 'done_all', color: AppTheme.primaryColor },
    ]

    const activeStatus = STATUS_PRIORITY.slice().reverse().find(({ key }) => !!item[key])

    const statusIcon = activeStatus && (
      <i
        className='material-icons-outlined'
        style={{ color: activeStatus.color, fontSize: 16 }}
      >
        {activeStatus.icon}
      </i>
    )

    const TOOLTIP_STATUS = [
      { key: 'sentTs', label: 'Sent', icon: 'done', color: 'white' },
      { key: 'deliveredTs', label: 'Delivered', icon: 'done_all', color: 'white' },
      { key: 'readTs', label: 'Read', icon: 'done_all', color: 'rgb(2, 70, 229)' },
    ]

    const tooltipContent = (
      <div>
        {TOOLTIP_STATUS.map(({ key, label, icon, color }) =>
          item[key] ? (
            <div key={key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <i className='material-icons-outlined' style={{ color, fontSize: 14 }}>
                {icon}
              </i>
              <span>{label}: {format(item[key])}</span>
            </div>
          ) : null
        )}
      </div>
    )

    let title = ''
    if(activeStatus && activeStatus.key === 'failedTs' && item && item.failedReasonParsed && Array.isArray(item.failedReasonParsed.errors) && item.failedReasonParsed.errors.length > 0 && item.failedReasonParsed.errors[0].message) {
      title = item.failedReasonParsed.errors[0].message
    } else if (!(activeStatus && activeStatus.key === 'createdTs')) {
      title = tooltipContent
    }

    const isIncomingMsg = ['new', 'reply'].includes(item.iType)
    return (
      <Tooltip
        title={title}
        placement='top'
        arrow
        classes={{ popper: css(Styles.tooltip) }}
        enterTouchDelay={0}
        leaveTouchDelay={3000}
      >
        <div className={css(!isIncomingMsg ? Styles.waStatusGroup : [Styles.waStatusGroup, Styles.waStatusGroupHidden])}>
          {statusIcon}
        </div>
      </Tooltip>
    )
  }

  renderWABAPhoneIcon = (wabaPhone, agentWaba) => {
    const numbersWaba = (phone) => {
      if (!phone) return null
      return String(phone).replace(/\D/g, '')
    }
    return (
      <Tooltip
        title={`Via WhatsApp Account: ${wabaPhone}`}
        placement='top'
        arrow
        classes={{ popper: css(Styles.tooltip) }}
        enterTouchDelay={0}
        leaveTouchDelay={3000}
      >
        <div className={css(Styles.wabaPhoneIcon)}>
          <i className='material-icons-outlined' style={{ fontSize: 16 }}>
              help_outline
          </i>
        </div>
      </Tooltip>
    )
  }

  renderWhatsAppStatus = (item) => {
    const format = (ts) => (ts ? Helper.formatTimestamp(ts) : '-')
    const statusTs = this.getWhatsAppMessageStatusTs(item)

    return (
      <div className={css(Styles.waStatusContainer)}>
        {statusTs && <span className={css(Styles.waStatusText)}>{format(statusTs)}</span>}
        {this.renderWhatsAppStatusIcon(item)}
      </div>
    )
  };

    handleTabChange = (tab, chartType) => {
      if(chartType === 'interactionsTable') {
        const tabKey = this.state.chartTabsData
          && this.state.chartTabsData.interactionsTable
          && this.state.chartTabsData.interactionsTable[tab]
          && this.state.chartTabsData.interactionsTable[tab].key
        if(tabKey === 'whatsapp') {
          notifyWAChatFocusChangeToNativeApp(this.crmUser, true)
          this.fetchWaMessages({isPolling: false, includeAgentWABA: true})
        } else {
          notifyWAChatFocusChangeToNativeApp(this.crmUser, false)
          if(tabKey === 'interactions') {
            this.fetchInteractions()
          }
        }
      }

      this.setState({
        selectedTabData: {
          ...this.state.selectedTabData,
          [chartType]: tab
        }
      })
    }

    renderRuleCreatorLink(eventSource, current) {
      const ruleName = eventSource.sourceName || current.creatorName || 'Automation Rule'
      const ruleId = eventSource.sourceId || current.creatorId
      const isSU = Controller.get().roleHelper().isOrgSU()
        || Controller.get().roleHelper().isProductSU()
      if (isSU && ruleId) {
        return (
          <a
            className={css(Styles.interactionInfoLink)}
            href={this.baseRoute() + '/automations/' + ruleId}
            target='_blank'
            rel='noopener noreferrer'
          >
            {ruleName}
          </a>
        )
      }
      return ruleName
    }

    renderInteractionInfo(current) {
      if (current.iType === 'CONTACT_CREATED') return current.contactOrigin

      const eventSource = current.payload && current.payload._eventSource
      if (eventSource && eventSource.sourceType === 'RULE') {
        return (
          <div className={css(Styles.interactionInfoMeta)}>
            <div className={css(Styles.interactionInfoLine)}>
              Automation: {this.renderRuleCreatorLink(eventSource, current)}
            </div>
          </div>
        )
      }

      let creator = ''
      if (eventSource && eventSource.sourceType === 'WORKFLOW') {
        creator = eventSource.sourceName || current.creatorName || 'Workflow'
      } else if (eventSource && eventSource.sourceType === 'SYSTEM') {
        creator = 'System'
      } else {
        creator = current.iType === 'BEMAIL' ? current.creatorName : (current.iType === 'FORM_SUBMISSION' || current.creatorId === this.crmUserId ? Helper.formatName(this.crmUser) : current.creatorName || Helper.formatName(this.staffUsers.find(u => u.id === current.creatorId || u.userId === current.creatorId)))
      }

      let sourceContent = null
      if (eventSource && eventSource.sourceType === 'STAFFUSER' && eventSource.jobTitle) {
        sourceContent = `Bulk (${eventSource.jobTitle})`
      } else if (current.emailSource) {
        sourceContent = current.emailSource === 'DRIP_CAMPAIGN' ? <a href={this.baseRoute() + '/campaigns/sequences/' + current.campaignId} target='_blank'><span>{current.emailSource}</span></a> : (current.emailSource === 'BROADCAST_CAMPAIGN' ? <a href={this.baseRoute() + '/campaigns/' + current.campaignId} target='_blank'><span>{current.emailSource}</span></a> : (current.emailSource === 'INBOX_CRON' ? 'INBOX' : current.emailSource))
      }

      const hasActivity = eventSource && eventSource.activityType && eventSource.activityType !== 'UPDATE_CONTACT'
      const hasChannel = eventSource && eventSource.sourceChannel
      const isUpdate = current.iType === 'UPDATE'
      const updateCount = isUpdate ? Object.keys((current.payload && current.payload.newObj) || {}).length : 0

      const emailClickTrail = UiHelper.renderEmailClickTrail(current.emailClickTrail || [])
      const showEmailStats = current.iType === 'BEMAIL' || (current.iType === 'EMAIL' && (current.emailSource === 'CONSOLE' || current.emailSource === 'DRIP_CAMPAIGN'))
      const agentEmail = getExtInteractionAgentEmail(current)

      return <div className={css(Styles.interactionInfoMeta)}>{[
        ...(creator ? [<div className={css(Styles.interactionInfoLine)}>Creator: {creator}</div>] : []),
        ...(agentEmail ? [<div className={css(Styles.interactionInfoLine)}>Agent: {agentEmail}</div>] : []),
        ...(sourceContent ? [<div className={css(Styles.interactionInfoLine)}>Source: {sourceContent}</div>] : []),
        ...(hasActivity ? [<div className={css(Styles.interactionInfoLine)}>Activity: {eventSource.activityType.replace(/_/g, ' ')}</div>] : []),
        ...(hasChannel ? [<div className={css(Styles.interactionInfoLine)}>Channel: {eventSource.sourceChannel}</div>] : []),
        ...(isUpdate ? [<div className={css(Styles.interactionInfoLine)}>{`${updateCount} field(s) updated`}</div>] : []),
        ...(showEmailStats ? [<div title={emailClickTrail} className={css(Styles.interactionInfoLine)}>{getEmailBounceCount(current) > 0 ? 'Email has bounced' : generateEmailStats(current)}</div>] : []),
      ]}
      </div>
    }

    renderWhatsAppInfo(current) {
      const creator = this.getWAMessageSender(current)
      return (<div>
        {creator && (<><span className={['new','reply'].includes(current.iType) ? css(Styles.line2, Styles.statusBadge, Styles.statusBadgeContact) : css(Styles.line2, Styles.statusBadge)}>{creator}</span></>)}
      </div>)
    }

    getInteractionSummary(current) {
      let summary = ''
      let imageLink = null
      let interactionName = current.iType
      switch (current.iType) {
      case 'NOTE':
        summary = Helper.renderHtmlText(current.noteRichText);
        imageLink = current.noteImageLink
        break;
      case 'UPDATE':
      {
        const eventSource = (current.payload && current.payload._eventSource) || {}
        if (eventSource.activityType === 'ROUTE_LEAD') {
          interactionName = 'LEAD ROUTED'
        } else if (eventSource.activityType === 'BULK_UPDATE') {
          interactionName = 'BULK UPDATE'
        } else if (eventSource.activityType === 'BULK_IMPORT') {
          interactionName = 'BULK IMPORT'
        } else if (eventSource.activityType === 'BULK_ENRICH') {
          interactionName = 'BULK ENRICH'
        } else if (eventSource.sourceType === 'WORKFLOW') {
          interactionName = 'CONTACT UPDATED (WORKFLOW)'
        } else {
          interactionName = 'CONTACT UPDATED'
        }

        let prefix = ''
        if (eventSource.sourceName && eventSource.sourceType !== 'RULE') {
          const ruleName = Helper.escapeHtml(String(eventSource.sourceName))
          prefix = `<p style="color: #666; font-size: 12px; margin-bottom: 4px;">Triggered by: <b>${ruleName}</b></p>`
        }
        const diff = UiHelper.getUpdateDiffSummary(current, this.crmUserFieldDef) || '<p>Contact attributes updated</p>'
        summary = prefix + diff
        break
      }
      case 'EVENT':
      {
        const p = current.payload || {}
        const ev = p._eventSource || {}
        if (ev.activityType === 'ENROL_IN_SEQUENCE') {
          interactionName = 'SEQUENCE ENROLMENT'
          let s = `<p>Sequence: <b>${Helper.escapeHtml(String(p.campaignTitle || p.campaignId || ''))}</b></p>`
          const inbox = p.inbox || p.inboxEmail || p.inboxName
          if (inbox) {
            s += `<p>Inbox: ${Helper.escapeHtml(String(inbox))}</p>`
          }
          const enrolmentId = p.enrolmentId || p.enrollmentId
          if (enrolmentId) {
            s += `<p>Enrolment ID: ${Helper.escapeHtml(String(enrolmentId))}</p>`
          }
          summary = s
        } else if (ev.activityType === 'SEND_META_CAPI') {
          interactionName = 'META CONVERSION (CAPI)'
          let s = `<p>Event: <b>${Helper.escapeHtml(String(p.capiEventName || ''))}</b></p>`
          if (p.testEventCode) {
            s += `<p>Test Event Code: ${Helper.escapeHtml(String(p.testEventCode))}</p>`
          }
          const pixelIds = p.pixelIds ? (Array.isArray(p.pixelIds) ? p.pixelIds.join(', ') : p.pixelIds) : p.pixelId
          if (pixelIds) {
            s += `<p>Pixel ID: ${Helper.escapeHtml(String(pixelIds))}</p>`
          }
          summary = s
        } else if (ev.activityType === 'REGISTER_ZOOM') {
          interactionName = 'ZOOM REGISTRATION (' + (p.zoomEventType || 'EVENT') + ')'
          let s = `<p>Event ID: <b>${Helper.escapeHtml(String(p.zoomEventId || ''))}</b></p>`
          if (p.registrantId) {
            s += `<p>Registrant ID: ${Helper.escapeHtml(String(p.registrantId))}</p>`
          }
          if (p.joinUrl) {
            s += `<p><a href="${Helper.escapeHtml(String(p.joinUrl))}" target="_blank" rel="noopener noreferrer">Join URL</a></p>`
          }
          summary = s
        } else if (ev.activityType === 'WEBHOOK_PUSH') {
          interactionName = 'WEBHOOK PUSH'
          let s = `<p>URL: <b>${Helper.escapeHtml(String(p.url || ''))}</b></p>`
          const status = p.responseStatus !== undefined ? p.responseStatus : (p.statusCode !== undefined ? p.statusCode : p.status)
          if (status !== undefined && status !== null && status !== '') {
            s += `<p>Status: ${Helper.escapeHtml(String(status))}</p>`
          }
          summary = s
        } else {
          interactionName = ev.activityType ? ev.activityType.replace(/_/g, ' ') : 'EVENT'
          summary = `<p>${Helper.escapeHtml(String(ev.sourceName || 'Automation event executed'))}</p>`
        }
        break
      }
      case 'FORM_SUBMISSION': {
        const body = (current.formSubmission || '').replaceAll('\n', '<br/>')
        const extra = formatFormSpecialEventData(current.formSubmissionRaw && current.formSubmissionRaw._formSpecialEventData)
        summary = extra ? (body ? body + '<br/>' + extra : extra) : body
        break
      }
      case 'EMAIL':
      {
        const from = current.emailFrom ? `<p>From: ${Helper.escapeHtml(current.emailFrom)}</p>` : ''
        const to = current.emailTo && current.emailTo.length > 0 ? `<p>To: ${(current.emailTo || []).map(e => Helper.escapeHtml(e)).join(', ')}</p>` : ''
        const cc = current.emailCC && current.emailCC.length > 0 ? `<p>Cc: ${(current.emailCC || []).map(e => Helper.escapeHtml(e)).join(', ')}</p>` : ''
        const bcc = current.emailBCC && current.emailBCC.length > 0 ? `<p>Bcc: ${(current.emailBCC || []).map(e => Helper.escapeHtml(e)).join(', ')}</p>` : ''
        const subject = `<p>Sub: ${Helper.escapeHtml(current.emailSubject)}</p>`
        const body = current.emailBodyHtmlText ? Helper.addHtmlTextUrlQueryParams(current.emailBodyHtmlText, {__slxignore: true}) : Helper.addRichTextUrlQueryParams(Helper.renderHtmlText(current.emailBodyRichText), {__slxignore: true})
        const hasAttachment = !!current.emailAttachment || !!current.emailAttachmentLink || (Array.isArray(current.emailAttachments) && current.emailAttachments.length > 0);
        const attachment = hasAttachment ? '<p style="font-size: 12px; color: gray;">Attachment: YES</p>' : '';
        summary = from + to + cc + bcc + subject + body + attachment;
        break;
      }
      case 'BEMAIL':
      {
        const emailRequest = (current.payload && current.payload.emailRequest) || {}
        const emailToRaw = current.emailTo || emailRequest.emailTo
        const emailToList = Array.isArray(emailToRaw) ? emailToRaw : (emailToRaw ? [emailToRaw] : [])
        const emailSubject = current.emailSubject || emailRequest.emailSubject || ''
        const from = current.emailFrom ? `<p>From: ${Helper.escapeHtml(current.emailFrom)}</p>` : ''
        const to = emailToList.length > 0 ? `<p>To: ${emailToList.map(e => Helper.escapeHtml(e)).join(', ')}</p>` : ''
        const subject = emailSubject ? `<p>Sub: ${Helper.escapeHtml(emailSubject)}</p>` : ''
        const hasAttachment = !!current.emailAttachment || !!current.emailAttachmentLink || (Array.isArray(current.emailAttachments) && current.emailAttachments.length > 0)
        const attachment = hasAttachment ? '<p style="font-size: 12px; color: gray;">Attachment: YES</p>' : ''
        summary = from + to + subject + attachment
        interactionName = 'BROADCAST EMAIL'
        break
      }
      case 'CONTACT_CREATED': summary = current.source; break;
      case 'WHATSAPP':
      {
        const parts = []
        if (current.payload && current.payload.waRequest) {
          if (current.payload.waRequest.fromNumber) { parts.push(`<p>From: ${current.payload.waRequest.fromNumber}</p>`) }
          if (current.payload.waRequest.template) { parts.push(`<p>Template: ${current.payload.waRequest.template}</p>`) }
        }

        const waResponseItem = current.payload.waResponse && Array.isArray(current.payload.waResponse.items) && current.payload.waResponse.items.length > 0 && current.payload.waResponse.items[0] ? current.payload.waResponse.items[0] : {}
        const waMessage = waResponseItem.message || {}
        let waSendStatus = 'ERROR'
        if (waResponseItem.error) {
          waSendStatus = 'ERROR'
        } else if (waMessage.message_status === 'pending') {
          waSendStatus = 'PENDING'
        } else if (waMessage.message_status === 'accepted' || waMessage.id) {
          waSendStatus = 'ACCEPTED'
        }
        const waErrorMessage = getWaErrorMessage(waResponseItem.error)
        parts.push(`<p>Status: ${waSendStatus}${waErrorMessage ? ` (${Helper.escapeHtml(waErrorMessage)})` : ''}</p>`)
        summary = parts.join('')
        break;
      }
      case 'WHATSAPP DIRECT':
      {
        const parts = []
        if (current.extpayload && current.extpayload.text && current.extpayload.text.trim()) {
          parts.push(`<p>${Helper.escapeHtml(current.extpayload.text)}</p>`)
        }
        summary = parts.join('')
        break;
      }
      case 'CALL OUTBOUND':
      {
        // One interaction per AI outbound call (e.g. Sarvam): created when placed, updated by the provider webhook.
        const co = (current.extpayload && current.extpayload.callOutbound) || {}
        const parts = []
        const statusLabels = {
          initiated: 'Call placed',
          api_error: 'Could not place call',
          connected: 'Connected',
          busy: 'Busy',
          no_answer: 'No answer',
          failed: 'Failed',
        }
        if (co.status) {
          const statusLabel = statusLabels[co.status] || String(co.status).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
          parts.push(`<p>Status: ${Helper.escapeHtml(statusLabel)}</p>`)
        }
        if (co.status === 'api_error' && co.error) {
          parts.push(`<p>Error: ${Helper.escapeHtml(String(co.error))}</p>`)
        } else if (co.failureReason) {
          parts.push(`<p>Reason: ${Helper.escapeHtml(String(co.failureReason))}</p>`)
        }
        if (co.durationSec != null && co.durationSec !== '' && !(co.status !== 'connected' && Number(co.durationSec) === 0)) {
          const sec = Math.round(Number(co.durationSec))
          const durLabel = Number.isFinite(sec)
            ? (sec >= 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`)
            : String(co.durationSec)
          parts.push(`<p>Duration: ${Helper.escapeHtml(durLabel)}</p>`)
        }
        if (co.intent) {
          parts.push(`<p>Intent: ${Helper.escapeHtml(String(co.intent))}</p>`)
        }
        if (co.toPhone) {
          parts.push(`<p>To: ${Helper.escapeHtml(String(co.toPhone))}</p>`)
        }
        if (co.recordingUrl) {
          const recUrl = Helper.escapeHtml(String(co.recordingUrl))
          parts.push(`<p><audio controls preload="none" src="${recUrl}" style="max-width:100%"></audio><br/><a href="${recUrl}" target="_blank" rel="noopener noreferrer">Recording</a></p>`)
        }
        if (co.summary) {
          parts.push(`<p>Summary: ${Helper.escapeHtml(String(co.summary))}</p>`)
        }
        if (co.transcriptText) {
          const truncatedNote = co.transcriptTruncated ? '<br/>(transcript truncated)' : ''
          parts.push(`<details><summary style="cursor:pointer">Transcript</summary><div style="white-space:pre-wrap">${Helper.escapeHtml(String(co.transcriptText))}${truncatedNote}</div></details>`)
        }
        summary = parts.join('')
        break;
      }
      // CALL ZOOM PHONE: click-to-call (legacy rows also carry webhook call data); ZOOM PHONE CALL LOG: webhook call log
      case 'CALL ZOOM PHONE':
      case 'ZOOM PHONE CALL LOG':
      {
        const zp = (current.extpayload && current.extpayload.zoomPhone) || {}
        const parts = []
        if (zp.direction) {
          parts.push(`<p>Direction: ${Helper.escapeHtml(String(zp.direction))}</p>`)
        }
        const connectedResults = ['answered', 'call_connected', 'connected', 'accepted', 'picked_up', 'succeeded']
        const notConnected = !!zp.callResult && !connectedResults.includes(zp.callResult)
        if (notConnected) {
          const resultLabel = String(zp.callResult).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
          parts.push(`<p>Status: ${Helper.escapeHtml(resultLabel)}</p>`)
        }
        if (zp.durationSec != null && zp.durationSec !== '' && !(notConnected && Number(zp.durationSec) === 0)) {
          const sec = Number(zp.durationSec)
          const mins = Number.isFinite(sec) ? Math.floor(sec / 60) : null
          const rem = Number.isFinite(sec) ? sec % 60 : null
          const durLabel = mins != null
            ? (mins > 0 ? `${mins}m ${rem}s` : `${rem}s`)
            : String(zp.durationSec)
          parts.push(`<p>Duration: ${Helper.escapeHtml(durLabel)}</p>`)
        }
        if (zp.callerNumber || zp.calleeNumber) {
          parts.push(`<p>From: ${Helper.escapeHtml(String(zp.callerNumber || ''))} → To: ${Helper.escapeHtml(String(zp.calleeNumber || ''))}</p>`)
        }
        if (zp.recordingUrl) {
          parts.push(`<p><a href="${Helper.escapeHtml(String(zp.recordingUrl))}" target="_blank" rel="noopener noreferrer">Recording</a></p>`)
        }
        if (zp.transcriptUrl) {
          parts.push(`<p><a href="${Helper.escapeHtml(String(zp.transcriptUrl))}" target="_blank" rel="noopener noreferrer">Transcript</a></p>`)
        }
        summary = parts.join('')
        break;
      }
      default:
      {
        if (current.payload && current.payload._workflow) {
          const workflow = current.payload._workflow
          const workflowData = current.payload._workflowData || {}
          const workflowResponse = current.payload._workflowResponse || []
          const workflowFieldDef = workflow.fieldDef
          interactionName = workflow.title
          const parts = []
          Object.keys(workflowData).forEach(k => {
            const field = UiHelper.withLinkedOptions(Helper.getField(workflowFieldDef, k), this.crmUserFieldDef)
            if (field && (workflowData[k] === 0 || workflowData[k])) {
              const value = workflowData[k];
              if (field.type === 'rich_text') {
                const noteVal = typeof value === 'string' ? value : JSON.stringify(value)
                parts.push(`<div>${Helper.escapeHtml(field.label)}: ${Helper.renderHtmlOrPlainText(noteVal)}</div>`)
              } else {
                const readableVal = (value === 0) ? '0' : UiHelper.getReadableVal(k, field, workflowData);
                parts.push(`<p>${field.label}: ${readableVal}</p>`)
              }
            }
          })
          summary = parts.join('')
        }
        break;
      }
      }

      const isBlockquotePresent = Helper.dohtmlStringcontainsTag(summary,'blockquote')
      summary = this.processEmailInteractionHtml(current,summary)

      return {summary,imageLink,interactionName,isBlockquotePresent}
    }

    renderInteractionEventStreamParenContent(eventKey, payload = {}) {
      if(eventKey) {
        if(payload && payload.formSubmissionRaw && payload.formSubmissionRaw.submission && payload.formSubmissionRaw.submission.message) {
          return(
            <span className={css(Styles.eventStreamEventName)}>
              {formatEventStreamFormMessage(payload.formSubmissionRaw.submission.message, css(Styles.eventStreamLink))}
            </span>
          )
        }
      }
      return null
    }

    renderInteractionEventStream(eventStream) {
      if (!Array.isArray(eventStream) || eventStream.length === 0) return null
      return (
        <div className={css(Styles.eventStreamBlock)}>
          {eventStream.map((entry, index) => {
            const eventKey = entry && entry.event ? String(entry.event) : ''
            const payload = entry && entry.payload
            const content = this.renderInteractionEventStreamParenContent(eventKey, payload)
            if(content) {
              return (
                <div key={index} className={css(Styles.eventStreamRow)}>
                  <div className={css(Styles.eventStreamTitleRow)}>{content}</div>
                </div>
              )
            }
          })}
        </div>
      )
    }

    openWebhookResponseDialog = (current) => {
      const webhookResponse = getWebhookPushResponse(current)
      if (!webhookResponse) return
      let json = ''
      try {
        json = JSON.stringify(webhookResponse, null, 2)
      } catch (err) {
        json = String(webhookResponse)
      }
      this.setState({
        webhookResponseDialogOpen: true,
        webhookResponseDialogJson: json,
      })
    }

    closeWebhookResponseDialog = () => {
      this.setState({
        webhookResponseDialogOpen: false,
        webhookResponseDialogJson: '',
      })
    }

    renderWebhookResponseDialog() {
      if (!this.state.webhookResponseDialogOpen) return null
      return (
        <PageDialog
          PageComponent={() => (
            <pre className={css(Styles.webhookResponseDialogContent)}>
              {this.state.webhookResponseDialogJson}
            </pre>
          )}
          title='Webhook response'
          isOpen={this.state.webhookResponseDialogOpen}
          onClosed={this.closeWebhookResponseDialog}
          maxWidth='md'
          fullWidth
        />
      )
    }

    renderInteraction(current) {
      const {summary,imageLink,interactionName,isBlockquotePresent} = this.getInteractionSummary(current)

      return (
        <div>
          <span className={css(Styles.line1)}>{interactionName}</span>
          <br/>
          {imageLink && (this.renderMediaContent({msgType: 'image',mediaLink: imageLink}))}
          <span className={css(Styles.line2, Styles.summaryContainer)} id={'id_' + current.__index} dangerouslySetInnerHTML={{ __html: summary }}></span>
          {current.iType === 'BEMAIL' && this.renderBroadcastEmailPreviewBox(current)}
          {(current.iType === 'EMAIL' || current.iType === 'BEMAIL') && isBlockquotePresent &&
            <span className={css(Styles.emailMoreBtn)} onClick={()=>this.handleEmailMoreBtnClick(current)}>
              <i className='material-icons-outlined'>more_horiz</i>
            </span>}
          {current.eventStream && this.renderInteractionEventStream(current.eventStream)}
        </div>
      )
    }

    renderInteractionInfoMobile = (current) => {
      const getSource = () => {
        let source = ''
        if(current.emailSource) {
          if(current.emailSource === 'INBOX_CRON') {
            source = 'INBOX'
          } else {
            source = current.emailSource
          }
          source = source && 'Source: ' + source
        }
        return source
      }

      const getEmailStats = () => {
        const statMsgs = []
        const showEmailStats = current.iType === 'BEMAIL' || (current.iType === 'EMAIL' && (current.emailSource === 'CONSOLE' || current.emailSource === 'DRIP_CAMPAIGN'))
        if (current && showEmailStats) {
          if(getEmailBounceCount(current) > 0) {
            statMsgs.push({ key: 'bounced', value: 'Status: Email has bounced'})
          } else {
            const payload = current.payload || {}
            const openCount = current.emailOpenCount || payload.openCount || 0
            const clickCount = current.emailClickCount || payload.clickCount || 0
            const replyCount = current.emailReplyCount || payload.replyCount || 0
            const isBroadcastEmail = current.iType === 'BEMAIL'
            if (isBroadcastEmail || current.emailOpenTracking || openCount > 0) {
              statMsgs.push({key: 'opens', value:`Opens: ${openCount}`})
            }
            if (isBroadcastEmail || current.emailClickTracking || clickCount > 0) {
              statMsgs.push({key: 'clicks', value: `Clicks: ${clickCount}`})
            }
            statMsgs.push({key: 'replies', value:`Replies: ${replyCount}`})
          }
        }
        return statMsgs
      }

      const TOOLTIP_INFO = []
      const source  = getSource()
      const emailStats = getEmailStats()

      if(source) {
        TOOLTIP_INFO.push({ key: 'source', label: 'Source', value: getSource()})
      }

      if(emailStats && emailStats.length > 0) {
        TOOLTIP_INFO.push(...emailStats)
      }

      const tooltipContent = TOOLTIP_INFO && TOOLTIP_INFO.length > 0 && (
        <div className={css(Styles.interactionInfoTooltipRows)}>
          {TOOLTIP_INFO.map(({ key, value }) => (
            <div key={key} className={css(Styles.interactionInfoLine)}>
              <span>{value}</span>
            </div>
          )
          )}
        </div>
      );

      if(!tooltipContent) {
        return null
      }

      return (
        <div className={css(Styles.interactionInfoContainer)}>
          <Tooltip
            title={tooltipContent}
            placement='top'
            arrow
            classes={{ popper: css(Styles.tooltip) }}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <div className={css(Styles.interactionInfoGroup)}>
              <i className='material-icons-outlined' style={{ color: AppTheme.primaryColor, fontSize: 24 }}>info</i>
            </div>
          </Tooltip>
        </div>
      );
    };

    renderInteractionMobile(current) {
      const getCreator = () => {
        let creator = ''
        const eventSource = current.payload && current.payload._eventSource
        if (eventSource && eventSource.sourceType === 'RULE') {
          creator = eventSource.sourceName || current.creatorName || 'Automation Rule'
        } else if (eventSource && eventSource.sourceType === 'WORKFLOW') {
          creator = eventSource.sourceName || current.creatorName || 'Workflow'
        } else if (eventSource && eventSource.sourceType === 'SYSTEM') {
          creator = 'System'
        } else if(current.iType === 'CONTACT_CREATED') {
          creator = current.contactOrigin || ''
        } else if (current.iType === 'BEMAIL') {
          creator = current.creatorName || ''
          if (!this.isMobileView) { creator = creator && 'Creator: ' + creator }
        } else if(current.iType === 'FORM_SUBMISSION' || current.creatorId === this.crmUserId) {
          creator = Helper.formatName(this.crmUser)
          if (!this.isMobileView) { creator = creator && 'Creator: ' + creator }
        } else {
          creator = current.creatorName || Helper.formatName(this.staffUsers.find(u => u.id === current.creatorId || u.userId === current.creatorId))
          if (!this.isMobileView) { creator = creator && 'Creator: ' + creator }
        }
        return creator
      }

      const creator = getCreator()
      const agentEmail = getExtInteractionAgentEmail(current)

      const {summary,imageLink,interactionName,isBlockquotePresent} = this.getInteractionSummary(current)
      return(<div className={css(Styles.chatRow)}>
        <div className={css([Styles.chatBubble, Styles.chatBubbleInteraction])}>
          <div className={css(Styles.chatBubbleMsgPart)}>
            <span className={css(Styles.chatBubbleTitle)}>{interactionName}</span>
            {imageLink && (this.renderMediaContent({msgType: 'image',mediaLink: imageLink}))}
            {(<div className={css(Styles.messageText)} dangerouslySetInnerHTML={{ __html: summary }}/>)}
            {current.iType === 'BEMAIL' && this.renderBroadcastEmailPreviewBox(current)}
            {(current.iType === 'EMAIL' || current.iType === 'BEMAIL') && isBlockquotePresent &&
            <span className={css(Styles.emailMoreBtn)} onClick={()=>this.handleEmailMoreBtnClick(current)}>
              <i className='material-icons-outlined'>more_horiz</i>
            </span>}
            {current.eventStream && this.renderInteractionEventStream(current.eventStream)}
          </div>
          <div className={css(Styles.interactionsMobileStatusContainer)}>
            <div className={css(Styles.waStatusText)}>{creator}</div>
            {agentEmail && <span className={css(Styles.waStatusText)}>Agent: {agentEmail}</span>}
            {current.createdTs && <span className={css(Styles.waStatusText)}>{Helper.formatTimestamp(current.createdTs)}</span>}
          </div>
        </div>
      </div>)
    }

    handleWAMedia({fromNumber, mediaId,mediaMime, action = 'open', waMsgId}) {
      this.skipScrollHandler = true
      this.setState({
        fetchState: ApiHelper.State.LOADING,
        loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
        inProcess: true,
        waMsgId: waMsgId
      })
      ApiHelper.callAwait({
        ms:  this.ms, method: 'GET', paths: ['miscs','get-wa-media'],
        queryParams: { orgId: this.orgId, fromNumber, mediaId, mediaMime },
      }).then(result => {
        if(result && result.link) {
          this.setState({
            fetchState: ApiHelper.State.READY,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
            inProcess: false,
            waMsgId: null,
          },()=>{
            this.fetchWaMessages({isPolling: true, includeAgentWABA: true})
          })
        } else {
          this.setState({
            fetchState: ApiHelper.State.READY,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
            inProcess: false,
            waMsgId: null,
            showToast: true,
            isErrorToast: true,
            toastMsg: Helper.getString('unableToOpenFile')
          })
        }
      }).catch(err => {
        this.setState({
          fetchState: ApiHelper.State.READY,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          inProcess: false,
          waMsgId: null
        })
      })

    }

    renderMediaContent(current) {
      const label = current.msgType === 'image' ? 'Open Image' : current.msgType === 'video' ? 'Open Video' : current.msgType === 'audio' ? 'Play Audio' : 'Open Document'

      if(this.state.inProcess &&  this.state.waMsgId === current.waMsgId) {
        return (<div className={css(Styles.mediaBubble)}>
          <CircularProgress size={24} className={css(Styles.progress)} />
        </div>)
      }

      if(current.mediaLink) {
        if(current.msgType === 'image') {
          return(<div
            className={css(Styles.mediaBubble)}>
            <img
              src={current.mediaLink}
              alt={current.mediaLink || 'Preview'}
              className={css(Styles.mediaPreview)}
            />
          </div>)
        } else if(current.msgType === 'video') {
          return(<div
            className={css(Styles.mediaBubble)}>
            <video
              src={current.mediaLink}
              controls
              controlsList='nodownload noplaybackrate'
              className={css(Styles.mediaPreview)}
            />
          </div>)
        } else if(current.msgType === 'audio') {
          return(<div
            className={css([Styles.mediaBubble,Styles.mediaAudioBubble])}>
            <audio
              src={current.mediaLink}
              controls
              controlsList='nodownload noplaybackrate'
              className={css(Styles.mediaAudioPreview)}
            />
          </div>)
        } else if(current.msgType === 'document') {
          return(<div
            className={css(Styles.mediaBubble)}
            onClick={()=>window.open(current.mediaLink, '_blank')}
          >
            <i className='material-icons-outlined'>description</i>
            {current.fileName && <span className={css(Styles.mediaLabel)}>{current.fileName}</span>}
            <span className={css(Styles.mediaLabelOpen)}>{label}</span>
          </div>)
        }
      }

      return (
        <div
          className={css(Styles.mediaBubble)}
          onClick={() =>
            this.handleWAMedia({
              fromNumber: current.fromNumber,
              mediaId: current.mediaId,
              mediaMime: current.mediaMime,
              waMsgId: current.waMsgId
            })
          }
        >
          <i className='material-icons-outlined'>
            {current.msgType === 'image' ? 'image' : current.msgType === 'video' ? 'play_circle' : current.msgType === 'audio' ? 'audio_file' : 'description'}
          </i>
          <span className={css(Styles.mediaLabelOpen)}>{label}</span>
        </div>
      )

    }

    getWAMessageSender(current) {
      const SENDERUSERID_DEPLOY_DATE = 1776255514000
      const MESSAGE_SENT_DATE = current.sentTs || current.failedTs || current.createdTs || current.ts || 0
      let sender = MESSAGE_SENT_DATE < SENDERUSERID_DEPLOY_DATE ? 'Agent' : 'Slixta Bot'
      if(['new','reply'].includes(current.iType)) {
        sender = Helper.formatName(this.crmUser)
      } else if(current.botPayload) {
        sender = 'Slixta Bot'
      } else if(current.senderUserId && Array.isArray(this.staffUsers)) {
        const staffUser = this.staffUsers.find(u => u.userId === current.senderUserId)
        if(staffUser) {
          sender = Helper.formatName(staffUser)
        }
        else {
          sender = 'Slixta Bot'
        }
      } else if(['outboundecho'].includes(current.iType)) {
        sender = 'Agent (WhatsApp Business App)'
      } else if(current.campaignId) {
        sender = 'Campaign'
      }
      return sender
    }


    renderWhatsAppMessage(current) {
      if(current) {
        const sender = this.getWAMessageSender(current)
        const mediaTypes = ['image', 'video', 'audio', 'document']

        if (current.obPayload && current.obPayload.type === 'interactive' && current.obPayload.contentBody) {
          const outgoing = current.obPayload.to === current.contactPhone
          const fromNumber = current.WABAPhone
          const agentWaba = this.state.agentWABA
          const session = WhatsappUtils.obPayloadContentBodyToSession(current.obPayload.contentBody)
          const statusTs = this.getWhatsAppMessageStatusTs(current)
          return (
            <div className={css(Styles.chatRow, outgoing ? Styles.chatRowOutgoing : Styles.chatRowIncoming)}>
              <div className={css(Styles.chatBubble,Styles.chatBubbleWhatsappPreview, outgoing ? Styles.bubbleOutgoing : Styles.bubbleIncoming)}>
                <div className={css(Styles.chatBubbleMsgPart)}>
                  <div className={css(Styles.senderNameContainer)}>
                    <div className={css(Styles.senderName)}>{sender}</div>
                    {this.renderWABAPhoneIcon(fromNumber, agentWaba)}
                  </div>
                  <WhatsappSessionPreview
                    messageBubbleMode
                    session={session}
                    bubbleTimeLabel={statusTs ? Helper.formatTimestamp(statusTs) : ''}
                    statusSlot={this.renderWhatsAppStatusIcon(current)}
                  />
                </div>
              </div>
            </div>
          )
        }

        if (current.obPayload && current.obPayload.type === 'template' && current.obPayload.template) {
          const outgoing = current.obPayload.to === current.contactPhone
          const fromNumber = current.WABAPhone
          this.ensureWaTemplatesForNumber(fromNumber)
          const previewComponents = this.getTemplatePreviewComponentsForMessage(current)
          const statusTs = this.getWhatsAppMessageStatusTs(current)
          if (previewComponents.length > 0) {
            return (
              <div className={css(Styles.chatRow, outgoing ? Styles.chatRowOutgoing : Styles.chatRowIncoming)}>
                <div className={css(Styles.chatBubble,Styles.chatBubbleWhatsappPreview, outgoing ? Styles.bubbleOutgoing : Styles.bubbleIncoming)}>
                  <div className={css(Styles.chatBubbleMsgPart)}>
                    <div className={css(Styles.senderNameContainer)}>
                      <div className={css(Styles.senderName)}>{sender}</div>
                      {this.renderWABAPhoneIcon(fromNumber, agentWaba)}
                    </div>
                    <WhatsappTemplatePreview
                      messageBubbleMode
                      components={previewComponents}
                      bubbleTimeLabel={statusTs ? Helper.formatTimestamp(statusTs) : ''}
                      statusSlot={this.renderWhatsAppStatusIcon(current)}
                    />
                  </div>
                </div>
              </div>
            )
          }
        }

        let isMediaMsg = false
        let mediaLink = null
        const fromNumber = current.WABAPhone
        const agentWaba = this.state.agentWABA
        const waMsgId = current.id
        let fileName = null
        let msgType = null
        let mediaId = null
        let mediaMime = null
        let text = null
        let outgoing = false
        if(current.parsed) {
          text = current.parsed.text
          msgType = current.parsed.msgType
          if(msgType) {
            isMediaMsg = mediaTypes.includes(msgType)
          }

          if(isMediaMsg) {
            mediaId = current.parsed.mediaId
            mediaMime = current.parsed.mediaMime

            if(current.original && Array.isArray(current.original.entry) && current.original.entry[0] && Array.isArray(current.original.entry[0].changes) && current.original.entry[0].changes[0] && current.original.entry[0].changes[0].value) {
              let messages = null
              if(Array.isArray(current.original.entry[0].changes[0].value.messages)) {
                messages = current.original.entry[0].changes[0].value.messages
              } else if(Array.isArray(current.original.entry[0].changes[0].value.message_echoes)) {
                messages = current.original.entry[0].changes[0].value.message_echoes
              }

              if(messages && messages[0] && current.parsed.msgType in messages[0]) {
                const message = messages[0][current.parsed.msgType]
                mediaId = message.id || mediaId
                mediaMime = message.mime_type || mediaMime
                text = message.caption || ''
                fileName = extractFilename(message.filename) || ''
                const _mediaCache = messages[0]._mediaCache
                if(_mediaCache && _mediaCache.link) {
                  mediaLink = _mediaCache.link
                }
              }
            }
          }
          outgoing = !['new','reply'].includes(current.iType) || false
        } else if(current.obPayload) {
          msgType = current.obPayload.type
          outgoing = current.obPayload.to === current.contactPhone
          if(msgType) {
            isMediaMsg = mediaTypes.includes(msgType)
          }

          if(isMediaMsg) {
            if(current.obPayload.contentBody) {
              mediaLink = current.obPayload.contentBody.link
              text = (current.obPayload.contentBody.caption || '')
              fileName = current.obPayload.contentBody.filename
            }
          } else if(msgType === 'text') {
            text = current.obPayload.contentBody && current.obPayload.contentBody.body
          }
        }

        if(current && current.failedTs && current.failedReasonParsed && Array.isArray(current.failedReasonParsed.errors) && current.failedReasonParsed.errors.length > 0) {
          mediaLink = null
        }

        if((current.parsed || current.obPayload) && msgType !== 'reaction') {
          return (
            <div className={css(Styles.chatRow, outgoing ? Styles.chatRowOutgoing : Styles.chatRowIncoming)}>
              <div className={css(Styles.chatBubble,Styles.chatBubbleWhatsappPreview, outgoing ? Styles.bubbleOutgoing : Styles.bubbleIncoming)}>
                <div className={css(Styles.chatBubbleMsgPart)}>
                  <div className={css(Styles.senderNameContainer)}>
                    <div className={css(Styles.senderName)}>{outgoing && !['Slixta Bot', 'Campaign', 'Agent', 'Agent (WhatsApp Business App)'].includes(sender) ? `Agent (${sender})` : sender}</div>
                    {this.renderWABAPhoneIcon(fromNumber, agentWaba)}
                  </div>
                  {isMediaMsg && (this.renderMediaContent({fromNumber,waMsgId,msgType,mediaId,mediaMime, mediaLink, fileName}))}
                  {text && (<div className={css(Styles.messageText)} dangerouslySetInnerHTML={{ __html: formatWAMessageText(text) }}/>)}
                </div>
                {this.renderWhatsAppStatus(current)}
              </div>
            </div>
          )
        }
      }

      return null
    }

    renderSendWAMessageRow = () => {
      const isSendBtnDisabled = !this.state.waTextMsg || !this.state.waTextMsg.trim()
      const waMessages = this.state.waMessages && JSON.parse(JSON.stringify(this.state.waMessages))
      const isWithin24Hours = isWithin24HoursWindow(waMessages,this.state.agentWABA)
      const onlyWAInteractionsMessageKey = getOnlyWAInteractionsMessageKey(waMessages, this.state.agentWABA)
      return (
        <div className={css(Styles.sendWAcontainer)}>
          {!isWithin24Hours && <p className={css(Styles.onlyWAInteractionsWarning)}>{Helper.getString(onlyWAInteractionsMessageKey)}</p>}
          {isWithin24Hours && <FormControl fullWidth margin='normal' className={css(Styles.formControl, this.state.waTextMsg && this.isMobileView && Styles.formControlExpanded)}>
            <Input
              type='text'
              multiline
              maxRows={4}
              disableUnderline
              placeholder= {this.isMobileView ? 'Message' : 'Type a message'}
              className={css(Styles.sendWAInput)}
              value={this.state.waTextMsg || ''}
              onChange={(e) => {
                this.setState({ waTextMsg: e.target.value })
              }}
              endAdornment={
                <InputAdornment
                  position='end'
                  className={css(Styles.sendWAEndAdornment)}
                >
                  {this.state.inSendWAMsgProcess && UiHelper.itemLoadingViewButton(28, {buttonStyles: Styles.buttonSendWALoading, progressIconStyles: Styles.sendWAIconContainerStyle })}
                  {!this.state.inSendWAMsgProcess  && <AppButton
                    icon='send'
                    buttonStyle={isSendBtnDisabled ? [Styles.buttonSendWAEnabled, Styles.buttonSendWADisabled] : Styles.buttonSendWAEnabled}
                    buttonTextStyle={isSendBtnDisabled ? Styles.buttonTextDisabled : Styles.buttonTextEnabled}
                    iconContainerStyle={[
                      Styles.selectedIconContainerStyle,
                      isSendBtnDisabled ? Styles.sendWAIconContainerStyleDisabled : Styles.sendWAIconContainerStyle
                    ]}
                    iconStyle={Styles.iconStyle}
                    onClick={!isSendBtnDisabled && this.sendTextWAMessage.bind(this,{waTextMsg: this.state.waTextMsg || ''})}
                  />}
                </InputAdornment>
              }
            />
          </FormControl>}
          <div className={css(Styles.mediaActionsWrapper, this.state.waTextMsg && this.isMobileView && Styles.mediaActionsHidden)}
          >
            {isWithin24Hours && <Tooltip title={'Send Image'}  placement='top-start' classes={{ popper: css(Styles.tooltip) }}>
              <span>
                <AppButton
                  icon='image'
                  buttonStyle={Styles.mediaButton}
                  iconContainerStyle={Styles.iconContainerStyle}
                  iconStyle={Styles.iconStyle}
                  onClick={this.openSendWAMessageDialog.bind(this, {defaultWAMessageType: 'IMAGE'})}
                />
              </span>
            </Tooltip>}
            {isWithin24Hours && <Tooltip title={'Send Document'}  placement='top-start' classes={{ popper: css(Styles.tooltip) }}>
              <span>
                <AppButton
                  icon='attach_file'
                  buttonStyle={Styles.mediaButton}
                  iconContainerStyle={Styles.iconContainerStyle}
                  iconStyle={Styles.iconStyle}
                  onClick={this.openSendWAMessageDialog.bind(this, {defaultWAMessageType: 'DOCUMENT'})}
                />
              </span>
            </Tooltip>}
            <Tooltip title={CRMHelper.getWaSendMessageActionTitle(this.orgId)}  placement='top-start' classes={{ popper: css(Styles.tooltip) }}>
              <span>
                {this.state.inOpenWaSendTemplateProcess && UiHelper.itemLoadingViewButton(24, {
                  buttonStyles: [Styles.mediaButton, Styles.mediaButtonLoading],
                  progressIconStyles: Styles.mediaButtonLoadingProgress,
                })}
                {!this.state.inOpenWaSendTemplateProcess && <AppButton
                  icon='post_add'
                  buttonStyle={Styles.mediaButton}
                  iconContainerStyle={Styles.iconContainerStyle}
                  iconStyle={Styles.iconStyle}
                  onClick={this.openSendWAMessageDialog.bind(this, {defaultWAMessageType: 'ANY'})}
                />}
              </span>
            </Tooltip>
          </div>
        </div>
      )
    }

    interactionsTableRowActions(key, cell, header) {
      if(this.getSelectedTabKey() === 'interactions') {
        const item = Array.isArray(cell) ? cell[cell.length - 1] : null
        if(item && item.iType) {
          return this.onActions(item)
        }
      }
    }

    getSelectedTabKey(tableType = 'interactionsTable') {
      const tabs = this.state && this.state.chartTabsData && this.state.chartTabsData[tableType]
      const selectedIndex = this.state && this.state.selectedTabData && this.state.selectedTabData[tableType]
      const tab = tabs && (selectedIndex || selectedIndex === 0) ? tabs[selectedIndex] : null
      return tab && tab.key
    }

    isWATabSelected() {
      return this.getSelectedTabKey() === 'whatsapp'
    }

    renderStatsSkeletonRow() {
      const count = Helper.isMobileView() ? 3 : 4
      const cards = Array.from({ length: count }, (_, index) => ({
        key: `stats-skeleton-${index}`,
        cardIndex: index + 1,
        padding: 'small',
        text: <span className={css(Styles.statsSkeletonValue)}>{'\u00a0'}</span>,
        subText: <span className={css(Styles.statsSkeletonLabel)}>{'\u00a0'}</span>,
      }))
      return this.renderStatCardRows([cards], Styles.statsCardContainerFull)
    }

    renderStatCardRows(rows, containerStyle = Styles.statsCardContainer) {
      return (
        <>
          {(!Helper.isMobileView() && rows && rows.length > 0) && (<div className={css(Styles.numericStatItemRow)}>
            {rows.map((row)=>(<Grid container className={css(Styles.numericStatContainerRow)}>
              {row.map((card)=>(
                <StatCard
                  xs={4}
                  sm={1}
                  key={card.key}
                  text={card.text}
                  subText={card.subText}
                  cardIndex={card.cardIndex}
                  padding={card.padding}
                  cardId={card.cardId}
                  handleCardClick={card.handleCardClick}
                  containerStyle={containerStyle}
                  cardStyle={Styles.statsCard}
                />))}
            </Grid>))}
          </div>)}
          {(Helper.isMobileView() && rows && rows.length > 0) && (<div className={css(Styles.numericStatItemRow)}>
            {rows.map((row, index) => {
              const rowGroups = Array.from({ length: Math.ceil(row.length / 3) })
              return (
                <Grid key={index}>
                  {rowGroups.map((_, groupIndex) => {
                    const groupItems = row.slice(groupIndex * 3, groupIndex * 3 + 3)
                    return (
                      <Grid
                        key={groupIndex}
                        container
                        className={css(Styles.numericStatContainerRow)}
                      >
                        {groupItems.map((card) => (
                          <StatCard
                            xs={12 / groupItems.length}
                            sm={12 / groupItems.length}
                            key={card.key}
                            text={card.text}
                            subText={card.subText}
                            cardIndex={card.cardIndex}
                            padding={card.padding}
                            cardId={card.cardId}
                            handleCardClick={card.handleCardClick}
                            containerStyle={containerStyle}
                            cardStyle={Styles.statsCard}
                          />
                        ))}
                      </Grid>
                    )
                  })}
                </Grid>
              )
            })}
          </div>)}
        </>
      )
    }

    renderSummaryCard() {
      const crmUserCp = this.state.crmUser && JSON.parse(JSON.stringify(this.state.crmUser))
      if(crmUserCp && !('linkedinConnectionStatus' in crmUserCp)) {
        crmUserCp.linkedinConnectionStatus = ''
      }
      if(crmUserCp && Array.isArray(crmUserCp.tags)) {
        crmUserCp.tags = crmUserCp.tags.sort((a, b) => a.localeCompare(b))
      }

      let crmUserFieldDef = JSON.parse(JSON.stringify(this.crmUserFieldDef))
      CRMHelper.appendContactInfoDisplayFields(crmUserFieldDef, {
        orgId: this.orgId,
        isLeadRoutingEnabled: !!AppHelper.getSetting(this.orgId, 'LEAD_ROUTING_TRIGGERS'),
        crmContactsStats: this.crmContactsStats,
      })

      const crmContactSummaryFieldsCdpSettings = AppHelper.getSetting(this.orgId, 'CRM_CONTACT_SUMMARY_FIELDS')
      if(crmContactSummaryFieldsCdpSettings) {
        const crmContactSummaryFields = crmContactSummaryFieldsCdpSettings.split(',').map(item => item.trim()).filter(Boolean)
        if(crmContactSummaryFields && crmContactSummaryFields.length > 0) {
          crmUserFieldDef = Helper.getFilteredFieldDef(crmUserFieldDef, crmContactSummaryFields)
        }
      }
      if (AppHelper.isABMEnabled(this.orgId) && this.accountFieldDef) {
        CRMHelper.appendAccountInfoFieldsToContactFieldDef(crmUserFieldDef, this.accountFieldDef)
      }
      if(crmUserFieldDef) {
        const accountsField = Helper.getField(crmUserFieldDef, 'accounts')
        if(accountsField) {
          accountsField.showOptionsChips = true
        }
        const primaryAccountIdField = Helper.getField(crmUserFieldDef, 'primaryAccountId')
        if(primaryAccountIdField) {
          primaryAccountIdField.showOptionsChips = true
        }
      }

      CRMHelper.markTextFieldsAsHyperlinks(crmUserFieldDef, crmUserCp)

      return (
        <SubEntityCard
          renderFieldsRowwise={true}
          title={'Summary'}
          formDefinition={crmUserFieldDef}
          item={crmUserCp || {}}
          displayFieldActions={this.displayFieldActions.bind(this)}
          hideTitle={true}
          noEdit={true}
          embedded={true}
          advancedView={true}
          advancedOpenByDefault={true}
          showAdvancedDivider={true}
        />
      )
    }

    renderAboveTable() {
      const configuredStatKeys = this.getConfiguredInteractionStatKeys()
      const waitingForStats = configuredStatKeys.length > 0 && !this.state.interactionStatsReady

      const valueCards = []
      if (AppHelper.isPaymentTransactionEnabled(this.orgId) && this.crmUser && this.crmUser.ltv > 0) {
        valueCards.push({
          key: 'totalAmountSpent',
          text: Helper.formatLargeNumber(this.crmUser.ltv || 0),
          subText: 'Value',
          cardIndex: 1,
          padding: 'small',
        })
      }

      const activityCards = []
      if (!waitingForStats && configuredStatKeys.length > 0 && Array.isArray(this.state.statsActivity)) {
        const cardsByLabel = {}
        configuredStatKeys.forEach((key) => {
          const stat = this.state.statsActivity.find(s => s.key === key)
          if (!stat) return
          let displayLabel = stat.label
          if (!displayLabel && Array.isArray(this.state.rawWorkflows)) {
            const matchedWorkflow = this.state.rawWorkflows.find(w => w.key === key)
            if (matchedWorkflow && matchedWorkflow.label) {
              displayLabel = matchedWorkflow.label
            }
          }
          if (!displayLabel) {
            displayLabel = key
          }
          if (cardsByLabel[displayLabel]) {
            cardsByLabel[displayLabel].text += (stat.doc_count || 0)
            cardsByLabel[displayLabel].keys.push(stat.key)
          } else {
            cardsByLabel[displayLabel] = {
              key: stat.key,
              keys: [stat.key],
              text: stat.doc_count || 0,
              subText: displayLabel,
              padding: 'small',
            }
          }
        })
        Object.values(cardsByLabel).forEach((card) => {
          activityCards.push({
            ...card,
            key: card.keys.join('|'),
            cardIndex: valueCards.length + activityCards.length + 1,
          })
        })
      }

      const allCards = [...valueCards, ...activityCards]
      const showStatsSection = waitingForStats || allCards.length > 0
      const cardCountForWidth = waitingForStats ? 1 : allCards.length
      const containerStyle = (!this.isMobileView && cardCountForWidth < 3)
        ? Styles.statsCardContainer
        : Styles.statsCardContainerFull

      if(configuredStatKeys.length === 0 && valueCards.length === 0) {
        return null
      }

      return (
        <div className={css(Styles.containerNarrow, Styles.containerNarrowAboveTable)}>
          <Grid container {...(this.isMobileView ? {} : {spacing: 4})}>
            {configuredStatKeys.length > 0 ? (
              <Grid item xs={12} sm={12}>
                <Collapse className={css(Styles.statsSectionCollapse)} in={showStatsSection} timeout={600} unmountOnExit>
                  {waitingForStats || allCards.length === 0
                    ? this.renderStatsSkeletonRow()
                    : this.renderStatCardRows(CRMHelper.splitCardsIntoBalancedRows(allCards, 7), containerStyle)
                  }
                </Collapse>
              </Grid>
            ) : (
              valueCards.length > 0 && (
                <Grid item xs={12} sm={12}>
                  {this.renderStatCardRows([valueCards], containerStyle)}
                </Grid>
              )
            )}
          </Grid>
        </div>
      );
    }

    renderInteractionTypeFilter() {
      if (this.isMobileView || this.getSelectedTabKey() !== 'interactions') {
        return null
      }
      return (
        <div className={css(Styles.showUpdateInteractions)}>
          <Checkbox
            onChange={(e) => {
              this.setState({ showUpdateInteractions: e.target.checked })
            }}
            name={'showUpdateInteractions'} color='primary'
            checked={!!this.state.showUpdateInteractions} />
          Updates
          <Checkbox
            onChange={(e) => {
              this.setState({ showEventInteractions: e.target.checked })
            }}
            name={'showEventInteractions'} color='primary'
            checked={!!this.state.showEventInteractions} />
          Events
        </div>
      )
    }

    renderBelowTable() {
      let tableTitle = ''
      let tableKey = null
      if(this.state && this.state.chartTabsData && this.state.chartTabsData.interactionsTable && this.state.selectedTabData && (this.state.selectedTabData.interactionsTable || this.state.selectedTabData.interactionsTable === 0)) {
        const chartTabData =  this.state.chartTabsData.interactionsTable[this.state.selectedTabData.interactionsTable]
        tableKey = chartTabData && chartTabData.key
        tableTitle = (chartTabData && chartTabData.title) ? chartTabData.title : ''
      }

      const tableData = this.getTableData(tableKey)
      return(
        <div className={css(Styles.containerNarrow)}>
          {this.isMobileView && this.renderCallDialog()}
          {this.isMobileView && this.renderMoreActionsDialog()}
          {this.renderBroadcastEmailPreviewDialog()}
          {this.renderWebhookResponseDialog()}
          <Grid container {...(this.isMobileView ? {} : {spacing: 4})}>
            <Grid item xs={12} sm={12}>
              <div
                ref={(el) => { this.waChatOverlayEl = el }}
                className={css(Styles.waChatOverlayHost)}
              >
                <TabTableCard
                  tableType={'interactionsTable'}
                  tabsData={this.state.chartTabsData}
                  tableTitle={tableTitle}
                  tableData={tableData}
                  selectedTabData={this.state.selectedTabData}
                  cellWidths={this.interactionsTableCellWidths}
                  disableTooltip={true}
                  showMoreInTable={true}
                  standardTabView={true}
                  showTabSeparator={true}
                  hideRowSeparators={this.isMobileView || this.getSelectedTabKey() !== 'interactions'}
                  hideTableHeader={this.isMobileView || this.getSelectedTabKey() !== 'interactions'}
                  inProcess={this.state.isTabTableLoading && this.getSelectedTabKey() !== 'summary'}
                  lockTabMinHeight={true}
                  handleTabChange={this.handleTabChange}
                  tableRowActions={this.interactionsTableRowActions.bind(this)}
                  renderTabActions={!this.isMobileView ? this.renderInteractionTypeFilter.bind(this) : null}
                  scrollHandler={this.scrollHandler.bind(this)}/>
              </div>
            </Grid>
          </Grid>
        </div>
      )
    }

    openBroadcastEmailPreview = (current) => {
      this.setState({
        broadcastEmailPreviewOpen: true,
        broadcastEmailPreviewHtml: getBroadcastEmailBody(current),
        broadcastEmailPreviewTitle: getBroadcastEmailSubject(current),
      })
    }

    closeBroadcastEmailPreview = () => {
      this.setState({
        broadcastEmailPreviewOpen: false,
        broadcastEmailPreviewHtml: '',
        broadcastEmailPreviewTitle: '',
      })
    }

    renderBroadcastEmailPreviewBox(current) {
      const body = getBroadcastEmailBody(current)
      if (!body) return null
      return (
        <div
          className={css(Styles.broadcastEmailPreviewBox)}
          onClick={() => this.openBroadcastEmailPreview(current)}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { this.openBroadcastEmailPreview(current) } }}
        >
          <div className={css(Styles.broadcastEmailPreviewInner)} dangerouslySetInnerHTML={{ __html: body }} />
        </div>
      )
    }

    renderBroadcastEmailPreviewDialog() {
      if (!this.state.broadcastEmailPreviewOpen) return null
      const previewHtml = this.state.broadcastEmailPreviewHtml
      return (
        <PageDialog
          PageComponent={() => (
            <div
              className={css(Styles.broadcastEmailPreviewDialogContent)}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          )}
          title={this.state.broadcastEmailPreviewTitle || 'BROADCAST EMAIL'}
          isOpen={this.state.broadcastEmailPreviewOpen}
          onClosed={this.closeBroadcastEmailPreview}
          maxWidth='lg'
          fullWidth
        />
      )
    }

    renderCallDialog() {
      if (!this.state.isCallDialogOpen) return null
      return (
        <PageDialog
          PageComponent={() => (
            <div className={css(Styles.actionDialogContainer)}>
              {this.state.callDialogOptions && this.state.callDialogOptions.map((option) => (
                <button
                  key={option.key}
                  className={css(Styles.actionDialogButton)}
                  onClick={() => this.handleCallOptionClick(option)}
                >
                  <p className={css(Styles.actionsDialogText)}>{option.label}</p>
                </button>
              ))}
            </div>
          )}
          title='Call Options'
          isOpen={this.state.isCallDialogOpen}
          onClosed={this.closeCallDialog}
          maxWidth='sm'
        />
      )
    }

    renderMoreActionsDialog() {
      if (!this.state.isMoreActionsDialogOpen) return null
      return (
        <PageDialog
          PageComponent={() => (
            <div className={css(Styles.actionDialogContainer)}>
              {this.state.moreActionsDialogOptions &&
                this.state.moreActionsDialogOptions.map((action) => (
                  <button
                    key={action.key}
                    className={css(Styles.actionDialogButton)}
                    onClick={() => this.handleMoreActionClick(action.key)}
                  >
                    <p className={css(Styles.actionsDialogText)}>
                      {action.label}
                    </p>
                  </button>
                ))}
            </div>
          )}
          title='More Actions'
          isOpen={this.state.isMoreActionsDialogOpen}
          onClosed={this.closeMoreActionsDialog}
          maxWidth='sm'
        />
      );
    }
    transform(result, crmUser) {
      if (!crmUser || !result || !result.items) return

      const crmUserCreator = this.staffUsers && this.staffUsers.find(u => u.id === crmUser.creatorId)
      const contactOrigin = `${(crmUser.createdOnFormSubmission ? 'FORM' : crmUser.createdByImport ? 'IMPORT' : 'CONSOLE')} ${crmUserCreator ? ` (${Helper.formatName(crmUserCreator)})` : ''}`

      const displayLabels = []
      if (crmUser.cognitoVerified) displayLabels.push('OTP VERIFIED')
      if (crmUser.emailAdminVerified) displayLabels.push('EMAIL VERIFIED')
      if (crmUser.phoneAdminVerified) displayLabels.push('PHONE VERIFIED')
      result.items.push({
        id: '0',
        iType: 'CONTACT_CREATED',
        createdTs: crmUser.createdTs,
        contactOrigin,
        source: crmUser.source,
        displayLabels,
      })
    }

    onCompleteAction(options) {
      const reqBody = options && options.apiInvokeData && options.apiInvokeData.reqBody
      const isClickToCall = reqBody && reqBody._isClickToCall
      const isWAITypesOnlyInteraction =  reqBody && reqBody.isWAITypesOnly
      if (isClickToCall) {
        this.setState({
          modalOpen: false,
          dialogApiData: options.apiInvokeData,
          showToast: true,
          isErrorToast: false,
          toastMsg: 'Call initiated'
        })
      } else if(isWAITypesOnlyInteraction) {
        const workflowKey = reqBody && reqBody.iType
        this.setState(prev => {
          const bumped = this.bumpInteractionStat(workflowKey, prev.statsActivity, prev.doc_count)
          return {
            modalOpen: false,
            items: [],
            dialogApiData: options.apiInvokeData,
            statsActivity: bumped.statsActivity,
            doc_count: bumped.doc_count,
          }
        }, () => {
          this.fetchWaMessages({ isPolling: false, includeAgentWABA: true })
        })
      }else {
        const iType = reqBody && reqBody.iType
        if (iType && this.workflows && this.workflows[iType] && this.workflows[iType]._hasWa) {
          this._refetchWaWithPageLoad = true
        }
        this.setState(prev => {
          const bumped = this.bumpInteractionStat(iType, prev.statsActivity, prev.doc_count)
          return {
            modalOpen: false,
            items: [],
            dialogApiData: options.apiInvokeData,
            statsActivity: bumped.statsActivity,
            doc_count: bumped.doc_count,
          }
        })
        this.fetchItems()
      }
    }
    openCallDialog(callOptions) {
      this.setState({
        isCallDialogOpen: true,
        callDialogOptions: callOptions
      })
    }

    closeCallDialog = () => {
      this.setState({ isCallDialogOpen: false })
    }

    openMoreActionsDialog(moreOptionsMobile) {
      this.setState({
        isMoreActionsDialogOpen: true,
        moreActionsDialogOptions: moreOptionsMobile
      })
    }

    closeMoreActionsDialog = () => {
      this.setState({ isMoreActionsDialogOpen: false })
    }

    handleCallOptionClick = (action) => {
      this.closeCallDialog()
      action.action()
    }

    handleMoreActionClick = (actionKey) => {
      this.closeMoreActionsDialog()
      if (actionKey === 'refresh') {
        this.fetchItems()
      } else if (actionKey === 'markAsUnread') {
        this.handleMarkAsUnread()
      }
    }
    openModalForAddStep1(inReplyToCrmInteraction, isReplyAll = false, isWAITypesOnly = false, isEmailButtonClick = false) {
      this.apiData = {
        orgId: this.orgId,
        ...AppHelper.getTeamBodyParams(this.orgId, '_staffSubOrgId'),
        userId: this.crmUserId,
        entityName: Helper.formatName(this.crmUser),
        isAddStep1: true,
        inReplyToCrmInteraction,
        isReplyAll,
        isWAITypesOnly,
      }

      if(!this.fieldDef) {
        return null
      }

      this.formDefinition = JSON.parse(JSON.stringify(this.fieldDef))
      this.formDefinition.title = isWAITypesOnly ? CRMHelper.getWaSendMessageActionTitle(this.orgId) : 'Add Interaction'
      this.formDefinition.formType = 'add'
      this.formDefinition.ctaButtonText = 'Next'
      this.formDefinition.disableAlwaysFormFields = this.formDefinition.disableAlwaysFormFields || []
      this.formDefinition.steps[0].fields = this.formDefinition.steps[0].fields.filter(f => ['iType', 'creatorId', 'emailFromInbox', 'emailInReplyToMessageId'].includes(f.key))
      const iTypeField = this.formDefinition.steps[0].fields.find(f => f.key === 'iType')
      const iTypeFieldOptions = []
      this.creatableTypes.forEach(ct => !iTypeFieldOptions.find(o => o.key === ct.key) ? iTypeFieldOptions.push({key: ct.key, label: ct.label}) : null)
      iTypeField.options = iTypeFieldOptions
      if (this.subOrgId) {
        if (!AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_TEAM_EMAIL_INTERACTION')) {
          iTypeField.options = iTypeField.options.filter(o => o.key !== 'EMAIL')
        }
      }
      if (this.isMobileView) { iTypeField.disableTyping = true }

      if(AppHelper.isSettingEnabled(this.orgId, 'CRM_CONTACT_EDIT_INTERACTION') && !isEmailButtonClick) {
        iTypeField.defaultValue = 'EDIT_CONTACT'
      } else if(isEmailButtonClick) {
        iTypeField.defaultValue = 'EMAIL'
      }

      if(isWAITypesOnly) {
        iTypeField.options = iTypeField.options.filter((option) => {
          if(option && option.key && this.workflows && option.key in this.workflows && this.workflows[option.key]._hasWa) {
            return true
          }
        })
        iTypeField.defaultValue = null
      }

      const creatorIdField = this.formDefinition.steps[0].fields.find(f => f.key === 'creatorId')
      if (creatorIdField) { creatorIdField.defaultValue = `${this.subOrgId || this.orgId}-${this.staffUserId}` }
      if (inReplyToCrmInteraction) {
        iTypeField.defaultValue = 'EMAIL'
        this.formDefinition.disableAlwaysFormFields.push('iType')
        const emailInReplyToMessageIdField = this.formDefinition.steps[0].fields.find(f => f.key === 'emailInReplyToMessageId')
        if (emailInReplyToMessageIdField) { emailInReplyToMessageIdField.defaultValue = inReplyToCrmInteraction.emailMessageId }
      } else {
        this.formDefinition.steps[0].fields = this.formDefinition.steps[0].fields.filter(f => f.key !== 'emailInReplyToMessageId')
      }
      this.formDefinition.disableAlwaysFormFields = ['creatorId', 'emailInReplyToMessageId']
      if(isEmailButtonClick) {
        this.formDefinition.disableAlwaysFormFields = [...this.formDefinition.disableAlwaysFormFields, 'iType']
      }
      this.formDefinition.steps[0].fields.push({
        key: 'emailTemplate',
        type: 'radio',
        label: 'Template',
        options: [{key: 'NONE', label: 'NONE'}].concat(this.crmEmailTemplates.filter((template)=> !template.campaignId).map(t => ({key: t.id, label: t.title}))),
        defaultValue: 'NONE',
        // eslint-disable-next-line camelcase
        v_required: { value: true, err: 'Provide email template' },
        placement: 'left',
        condition: { key: 'iType', value: 'EMAIL', values: ['EMAIL'] },
      })
      this.formDefinition.steps[0].fields.push({
        key: 'emailSignature',
        type: 'radio',
        label: 'Include Signature',
        options: [{key: 'YES', label: 'YES'}, {key: 'NO', label: 'NO'}],
        defaultValue: 'YES',
        // eslint-disable-next-line camelcase
        v_required: { value: true, err: 'Provide signature' },
        placement: 'right',
        condition: { key: 'iType', value: 'EMAIL', values: ['EMAIL'] },
      })

      if (isWAITypesOnly && CRMHelper.isWaQuickMessageEnabled(this.orgId)) {
        this.finishOpenModalForAddStep1WA()
        return
      }

      this.showAddStep1TypePicker(iTypeField, creatorIdField, isEmailButtonClick)
    }

    showAddStep1TypePicker(iTypeField, creatorIdField, isEmailButtonClick = false) {
      if(this.isMobileView && !isEmailButtonClick) {
        const customPageDialogProps = {
          pageTitle: 'Interactions',
          items: iTypeField.options,
          CustomPageDialogComp: ButtonList,
          onButtonClick: (action)=> {
            const options = {
              formData: {
                iType: action.key,
                creatorId: creatorIdField.defaultValue,
                ...this.apiData,
              }
            }
            this.setState({
              customPageDialogOpen: false
            },()=>{
              if(action && action.key === 'EMAIL') {
                this.openModalForAddStep1(null,false,false,true)
              } else {
                this.customSubmitForAddStep1(options)
              }
            })
          }
        }
        this.openModalForCustomPageDialog(customPageDialogProps)
      } else {
        this.setState({
          modalOpen: true
        });
      }
    }

    ensureWaContentsLoaded = async () => {
      if (Array.isArray(this.waContents)) return this.waContents
      if (this.waContentsFetchPromise) return this.waContentsFetchPromise
      this.waContentsFetchPromise = ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['wacontents'],
        queryParams: { orgId: this.orgId },
      }).then((data) => {
        this.waContents = Array.isArray(data && data.items) ? data.items : []
        this.waContentsFetchPromise = null
        return this.waContents
      }).catch((err) => {
        this.waContentsFetchPromise = null
        throw err
      })
      return this.waContentsFetchPromise
    }

    finishOpenModalForAddStep1WA = async () => {
      this._waSendStep1LoadId = (this._waSendStep1LoadId || 0) + 1
      const loadId = this._waSendStep1LoadId

      await (this._waSendPickerPrefetchPromise || this.prefetchWaSendPickerData(this.state.agentWABA))
      this._waSendPickerReady = true
      this._waSendPickerPrefetchPromise = null

      if (loadId !== this._waSendStep1LoadId) return

      const agentWABA = this.state.agentWABA
      const isSessionActive = isWithin24HoursWindow(this.state.waMessages, agentWABA)
      const contentOptions = buildWaSendPickerContentOptions(this.waContents, {
        agentWABA,
        isSessionActive,
      })

      const unified = buildWaSendUnifiedMessageOptions({ contentOptions })

      this.formDefinition = buildWaSendStep1FormDefinition({
        messageOptions: unified.options,
        marketingKeys: unified.marketingKeys,
        waContentPreviewById: {},
      })

      if (this.isMobileView) {
        modifyFormDefForMobile(this.formDefinition)
        const messageField = Helper.getField(this.formDefinition, 'waMessageId')
        if (messageField) {
          messageField.disableTyping = false
          messageField.disableClearable = false
        }
      }

      this.setState({ modalOpen: true, inOpenWaSendTemplateProcess: false })
      this.loadWaSendStep1Content(loadId)
    }

    loadWaSendStep1Content = async (loadId) => {
      try {
        await this.ensureWaContentsLoaded()
      } catch (e) {
        this.waContents = []
      }

      const agentWABA = this.state.agentWABA
      const isSessionActive = isWithin24HoursWindow(this.state.waMessages, agentWABA)
      const contentOptions = buildWaSendPickerContentOptions(this.waContents, {
        agentWABA,
        isSessionActive,
      })
      const unified = buildWaSendUnifiedMessageOptions({ contentOptions })

      const previewTemplateValues = CRMHelper.buildNonEmptyContactTemplateValues(
        this.crmUser,
        this.crmUserFieldDef,
        'contact'
      )

      const substituteTemplateVarsForPreview = (components) => {
        const vars = Array.isArray(components)
          ? components
          : (components && typeof components === 'object' ? Helper.dictToArray(components) : [])
        return vars.map((v) => {
          if (!v || typeof v !== 'object' || v.text == null) return v
          return { ...v, text: CRMHelper.substituteContactPlaceholders(String(v.text), previewTemplateValues) }
        })
      }

      const waContentPreviewById = {}
      const addTemplatePreviews = (options) => {
        for (const opt of (options || [])) {
          const content = (this.waContents || []).find(c => c && c.id === opt.key)
          if (!content) continue
          const waTemplate = getWaTemplateFromContent(content)
          let previewComponents = []
          if (waTemplate) {
            try {
              let structure = waTemplate.components
              if (!Array.isArray(structure)) {
                structure = structure && typeof structure === 'object' ? Helper.dictToArray(structure) : []
              }
              previewComponents = buildPreviewComponentsFromTemplate(
                structure,
                substituteTemplateVarsForPreview(content.components),
                { hideTemplateExamplesWhenUnset: true }
              )
            } catch (e) {
              previewComponents = []
            }
          }
          waContentPreviewById[opt.key] = { kind: 'template', previewComponents }
        }
      }

      addTemplatePreviews(contentOptions.marketing)
      addTemplatePreviews(contentOptions.utility)

      if (loadId !== this._waSendStep1LoadId || !this.state.modalOpen) return

      const fields = this.formDefinition && this.formDefinition.steps && this.formDefinition.steps[0]
        ? this.formDefinition.steps[0].fields
        : []
      const messageField = fields.find(f => f.key === 'waMessageId')
      const marketingWarningField = fields.find(f => f.key === 'waMarketingDeliveryWarning')
      if (messageField) messageField.options = unified.options
      if (marketingWarningField) {
        marketingWarningField.condition = { key: 'waMessageId', values: unified.marketingKeys }
      }

      this.formDefinition = {
        ...this.formDefinition,
        waContentPreviewById,
      }
      this.setState({})
    }

    openModalForAdd(fieldDef, inReplyToCrmInteraction, isReplyAll, apiData = {}, iType) {
      this.apiData = {
        orgId: this.orgId,
        ...AppHelper.getTeamBodyParams(this.orgId, '_staffSubOrgId'),
        userId: this.crmUserId,
        entityName: Helper.formatName(this.crmUser),
        ...apiData,
      }
      const fieldDefCp = JSON.parse(JSON.stringify(fieldDef))
      fieldDefCp.steps[0].fields = fieldDefCp.steps[0].fields.filter(f => {
        if (f.condition && f.condition.key === 'iType' && Array.isArray(f.condition.values) && !f.condition.values.includes(iType)) {
          return false
        }
        return true
      })

      fieldDefCp.disableAlwaysFormFields = ['iType', 'creatorId', 'formSubmission', 'emailFromInbox', 'emailInReplyToMessageId']
      fieldDefCp.steps[0].fields = fieldDefCp.steps[0].fields.filter(f => f.key !== 'emailFrom')
      const emailFromInboxField = fieldDef.steps[0].fields.find(f => f.key === 'emailFromInbox') || {}
      const emailFromInbox = this.crmInboxes.find(inbox => inbox.id === emailFromInboxField.defaultValue) || {}
      if (inReplyToCrmInteraction) {
        const inReplyToEmailTo = inReplyToCrmInteraction.emailTo || []
        const inReplyToEmailCc = inReplyToCrmInteraction.emailCC || []
        const inReplyToEmailBcc = inReplyToCrmInteraction.emailBCC || []
        const inReplyToEmailFrom = [inReplyToCrmInteraction.emailFrom]
        const inReplyToSentByMe = this.crmInboxes.some(i => Helper.isEmailEqual(inReplyToCrmInteraction.emailFrom || '', i.email))
        fieldDefCp.steps[0].fields.forEach(f => {
          if (f.key in inReplyToCrmInteraction && ['emailTo', 'emailCC', 'emailBCC'].includes(f.key)) {
            const otherOptions = [...inReplyToEmailTo, ...inReplyToEmailCc, ...inReplyToEmailBcc].map(e => ({key: Helper.extractEmailAddress(e), label: e}))
            f.options = Array.from(new Map([...otherOptions, ...(JSON.parse(JSON.stringify(f.options)) || [])].map(obj => [obj.key, obj])).values())
            if (isReplyAll) {
              if (f.key === 'emailTo') {
                f.defaultValue = [this.crmUser.email]
              } else if (f.key === 'emailCC') {
                const defaultValueCC = Array.from(new Map([...otherOptions].map(obj => [obj.key, obj])).values())
                f.defaultValue = defaultValueCC.map((option) => {
                  if(![this.crmUser.email, emailFromInbox.email].includes(option.key)) return option.key
                }).filter(a => !!a)
              } else if (f.key === 'emailBCC') {
                f.defaultValue = []
              }
            } else {
              if (f.key === 'emailTo') {
                f.defaultValue = [this.crmUser.email]
              } else if (!['emailCC', 'emailBCC'].includes(f.key)) {
                f.defaultValue = inReplyToCrmInteraction[f.key]
              }
            }
          }
        })
        const emailSubjectField = fieldDefCp.steps[0].fields.find(f => f.key === 'emailSubject')
        if (emailSubjectField) { emailSubjectField.defaultValue = inReplyToCrmInteraction.emailSubject.startsWith('Re: ') ? inReplyToCrmInteraction.emailSubject : `Re: ${inReplyToCrmInteraction.emailSubject}` }
        if(fieldDefCp && emailFromInbox && emailFromInbox.email && AppHelper.isSettingEnabled(this.orgId, 'CRM_INBOX_DEFAULT_FOR_REPLY_EMAIL')) {
          populateEmailFormDefFromSettings(fieldDefCp,emailFromInbox.email,this.orgId)
        }
      } else {
        fieldDefCp.steps[0].fields = fieldDefCp.steps[0].fields.filter(f => f.key !== 'emailInReplyToMessageId')
        if(fieldDefCp && emailFromInbox && emailFromInbox.email) {
          populateEmailFormDefFromSettings(fieldDefCp,emailFromInbox.email,this.orgId)
        }
      }

      if (iType in (this.workflows || {})) {
        fieldDefCp.steps[0].fields.push(...this.workflows[iType].fieldDef.steps[0].fields)
        if (Array.isArray(this.workflows[iType].fieldDef.disableAlwaysFormFields)) {
          fieldDefCp.disableAlwaysFormFields.push(...this.workflows[iType].fieldDef.disableAlwaysFormFields)
        }

        if(this.workflows[iType]._hasWa) {
          fieldDefCp.description = Helper.getString('sendWhatsAppMessage')
          fieldDefCp.customStyles = {
            modalDescriptionStyles: Styles.customModalDescriptionStyles,
          };
        }
      }
      super.openModalForAdd(fieldDefCp, false)
    }

    onAdd() {
      this.setState({
        apiMethod: 'POST',
        apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminteractions'], queryParams: { orgId: this.orgId, ...(AppHelper.isTeamsUnifiedView(this.orgId) ? AppHelper.getTeamApiParams(this.orgId) : {}) } }),
      });
    }

    openModalForEditSummary = async () => {
      const current = this.state.crmUser && JSON.parse(JSON.stringify(this.state.crmUser))
      if (!current) { return }
      current.email = this.state.crmUser.email
      const needsStats = !this.crmContactsStats && AppHelper.isCrmContactEditUtmCampaignEnabled(this.orgId)
      if (needsStats) {
        this.setState({
          fetchState: ApiHelper.State.LOADING,
          loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
        })
      }
      try {
        this.crmContactsStats = await CRMHelper.ensureCrmContactsStats({
          orgId: this.orgId,
          subOrgId: this.subOrgId,
          crmContactsStats: this.crmContactsStats,
        })
        return this.openModalForEdit(
          current,
          CRMHelper.buildCrmContactEditFieldDef(this.crmUserFieldDef, {
            orgId: this.orgId,
            isMobileView: this.isMobileView,
            crmContactsStats: this.crmContactsStats,
          })
        )
      } finally {
        if (needsStats) {
          this.setState({
            fetchState: ApiHelper.State.READY,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          })
        }
      }
    }

    openModalForEdit(current, formDefinition) {
      this.apiData = {
        orgId: this.orgId,
        ...AppHelper.getTeamBodyParams(this.orgId, '_staffSubOrgId'),
        userId: this.crmUserId,
      }
      if (formDefinition) {
        this.formDefinition = formDefinition
        this.formDefinition.formType = 'edit'

        Helper.populateFormDefinitionValues(this.formDefinition, current)
        CRMHelper.normalizeAssigneeFieldValues(this.formDefinition, ['assignee'])
        UiHelper.removeHiddenCrmContactStates(Helper.getField(this.formDefinition, 'state'), this.orgId)
        CRMHelper.populateAssigneeFieldOptions(formDefinition,this.subOrgs,this.orgId,this.teamsUnifiedView ? '' : this.subOrgId,false,this.staffUsers)
        this.setState({
          apiMethod: 'POST',
          apiEndPoint: ApiHelper.makeUrlPath2({
            ms: AppConfig.CDP_MS.ENDPOINT,
            paths: ['miscs', 'update-crm-contact'],
            queryParams: { id: current.id, orgId: this.orgId, ...AppHelper.getTeamApiParams(this.orgId, 'staffSubOrgId') }
          }),
        });
        this.setState({ modalOpen: true })
      } else {
        const fieldDefCp = JSON.parse(JSON.stringify(this.fieldDef))
        fieldDefCp.steps[0].fields = fieldDefCp.steps[0].fields.filter(f => f.key !== 'emailFromInbox')
        const emailFromField = fieldDefCp.steps[0].fields.find(f => f.key === 'emailFrom')
        if (emailFromField && emailFromField.options.every(o => o.key !== current.emailFrom)) {
          emailFromField.options.push({key: current.emailFrom, text: current.emailFrom, label: current.emailFrom})
        }
        const emailAttachmentField = fieldDefCp.steps[0].fields.find(f => f.key === 'emailAttachment')
        if (emailAttachmentField) {
          emailAttachmentField.forceAllowFileOpen = true
        }
        super.openModalForEdit(current, {
          steps: fieldDefCp.steps,
          disableAlwaysFormFields: ['iType', 'creatorId', 'formSubmission', 'emailFrom', 'emailTo', 'emailCC', 'emailBCC', 'emailSubject', 'emailBodyRichText', 'emailAttachment', 'emailAttachments', 'emailInReplyToMessageId'],
        });
      }
    }

    onEdit(current) {
      CRMHelper.normalizeAssigneeFieldValues(this.formDefinition, ['noteMentions'])
      this.setState({
        apiMethod: 'PUT',
        apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminteractions', current.id], queryParams: { orgId: this.orgId, ...(AppHelper.isTeamsUnifiedView(this.orgId) ? AppHelper.getTeamApiParams(this.orgId) : {}) } }),
      });
    }

    openModalForDelete(current, fieldDef) {
      this.apiData = {
        orgId: this.orgId,
        ...AppHelper.getTeamBodyParams(this.orgId, '_staffSubOrgId'),
        userId: this.crmUserId,
      }
      super.openModalForDelete(current, fieldDef)
    }

    onDelete(current) {
      this.setState({
        apiMethod: 'DELETE',
        apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminteractions', current.id], queryParams: { orgId: this.orgId, ...(AppHelper.isTeamsUnifiedView(this.orgId) ? AppHelper.getTeamApiParams(this.orgId) : {}) } }),
      });
    }

  onActions = (current) => {
    const actions = []
    if (getWebhookPushResponse(current)) {
      actions.push(<Tooltip title={'View'} placement='top-start' key={current.id + 'webhookResponse'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openWebhookResponseDialog(current)}>visibility</i>
        </span>
      </Tooltip>)
    }
    if (EDITABLE_TYPES.includes(current.iType)) {
      actions.push(UiHelper.buttonEdit(this, current))
      actions.push(UiHelper.buttonDelete(this, current))
    } else if (VIEWABLE_TYPES.includes(current.iType)) {
      actions.push(<Tooltip title={'View'} placement='top-start' key={current.id + 'viewFormSub'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForEdit(current)}>visibility</i>
        </span>
      </Tooltip>)
      if (current.iType === 'EMAIL') {
        actions.push(<Tooltip title={'Reply'} placement='top-start' key={current.id + 'replyEmail'}>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i className='material-icons-outlined' onClick={() => this.openModalForAddStep1(current)}>reply</i>
          </span>
        </Tooltip>)
        const hasMultipleEmailTo = Array.isArray(current.emailTo) && current.emailTo.length > 1
        const hasEmailCc = Array.isArray(current.emailCC) && current.emailCC.length > 0
        const hasEmailBcc = Array.isArray(current.emailBCC) && current.emailBCC.length > 0
        if (hasMultipleEmailTo || hasEmailCc || hasEmailBcc) {
          actions.push(<Tooltip title={'Reply All'} placement='top-start' key={current.id + 'replyAllEmail'}>
            <span className={css(AppStyles.entityTableLinkIcon)}>
              <i className='material-icons-outlined' onClick={() => this.openModalForAddStep1(current, true)}>reply_all</i>
            </span>
          </Tooltip>)
        }
      }
    } else if (current.payload && current.payload._workflow && Array.isArray(current.payload._workflow.actions)) {
      const pcActionIndex = current.payload._workflow.actions.findIndex(action => action.op === 'PUSH_TSEVENT' && action.eventKey === 'paymentConfirmation')
      if (pcActionIndex !== -1) {
        const pcAction = current.payload._workflow.actions[pcActionIndex]
        if (pcAction && pcAction.payloadFields && pcAction.payloadFields.transactionAmount) {
          const formData = Object.entries(current.payload._workflowData || {}).reduce((p, c) => {
            p[`form.${c[0]}`] = c[1]
            return p
          }, {})
          const pcTransactionAmount = Number.parseFloat(Helper.replaceText(pcAction.payloadFields.transactionAmount, formData))
          if (!Number.isNaN(pcTransactionAmount)) {
            actions.push(<Tooltip title={'Edit'} placement='top-start' key={current.id + 'edit-pcTransactionAmount'}>
              <span className={css(AppStyles.entityTableLinkIcon)}>
                <i className='material-icons-outlined' onClick={() => this.openModalForEditPaymentConfirmation(current, pcTransactionAmount)}>edit</i>
              </span>
            </Tooltip>)
          }
        }
      }
    }
    return actions
  }

  async onFormFieldOptionAdd(options) {
    const { formField, value, formId } = options
    if (this.fieldDef.formId === formId) {
      formField.options.push({
        key: value,
        label: value
      })
      return formField.options || []
    }
    return []
  }

  openSendWAMessageDialog({defaultWAMessageType = null}) {
    if(defaultWAMessageType && ['IMAGE','DOCUMENT'].includes(defaultWAMessageType)) {
      const formData = {
        iType: 'WHATSAPP',
        isAddStep1: true,
        orgId: this.orgId,
      }
      this.customSubmitForAddStep1({formData, defaultWAMessageType})
    } else if(defaultWAMessageType === 'ANY') {
      if (!CRMHelper.isWaQuickMessageEnabled(this.orgId) || this._waSendPickerReady) {
        this.openModalForAddStep1(null, false, true)
        return
      }
      if (this.state.inOpenWaSendTemplateProcess) return
      const loadId = ++this._openWaSendTemplateLoadId
      this.setState({ inOpenWaSendTemplateProcess: true }, async () => {
        try {
          await (this._waSendPickerPrefetchPromise || this.prefetchWaSendPickerData(this.state.agentWABA))
          this._waSendPickerReady = true
          this._waSendPickerPrefetchPromise = null
        } catch (e) {
          this._waSendPickerReady = true
          this._waSendPickerPrefetchPromise = null
        }
        if (loadId !== this._openWaSendTemplateLoadId) return
        this.openModalForAddStep1(null, false, true)
      })
    }
  }

  customSubmitForAddStep1(options) {
    const { formData, defaultWAMessageType = null } = options
    const waContentId = getWaContentIdFromIType(formData && formData.waMessageId)
    if (waContentId) {
      const toNumber = this.crmUser && this.crmUser.phone
      if (!toNumber) {
        this.setState({
          fetchState: ApiHelper.State.READY,
          modalOpen: false,
          showToast: true,
          isErrorToast: false,
          toastMsg: Helper.getString('whatsAppUnavailableForContact'),
        })
        return
      }
      delete formData.isAddStep1
      delete formData.inReplyToCrmInteraction
      delete formData.isReplyAll
      formData.isSendWAContent = true
      formData.waContentId = waContentId
      formData.toNumber = toNumber
      formData.subOrgId = this.crmUser && this.crmUser.subOrgId
      formData.orgId = formData.orgId || this.orgId
      return this.customSubmitForSendWAContent(options)
    }
    if (formData && formData.waMessageId) {
      formData.iType = formData.waMessageId
    }

    let fieldDef = JSON.parse(JSON.stringify(this.fieldDef))
    const inReplyToCrmInteraction = formData.inReplyToCrmInteraction
    const isReplyAll = formData.isReplyAll

    delete formData.isAddStep1
    delete formData.inReplyToCrmInteraction
    delete formData.isReplyAll

    const apiData = {}
    fieldDef.steps[0].fields.find(f => f.key === 'iType').defaultValue = formData.iType
    const iTypeField = fieldDef.steps[0].fields.find(f => f.key === 'iType')
    this.creatableTypes.forEach(ct => !iTypeField.options.find(o => o.key === ct.key) && iTypeField.options.push({key: ct.key, label: ct.label}))
    fieldDef.steps[0].fields.find(f => f.key === 'creatorId').defaultValue =
      formData.creatorId || `${this.subOrgId || this.orgId}-${this.staffUserId}`
    fieldDef.steps[0].fields.find(f => f.key === 'emailInReplyToMessageId').defaultValue = formData.emailInReplyToMessageId || ''
    fieldDef.steps[0].fields.find(f => f.key === 'emailFromInbox').defaultValue = formData.emailFromInbox
    if (formData.iType === 'EMAIL') {
      apiData.emailSource = 'CONSOLE'
      fieldDef.ctaButtonText = 'Send'
      if (formData.emailTemplate !== 'NONE') {
        const emailTemplate = this.crmEmailTemplates.find(t => t.id === formData.emailTemplate)
        fieldDef.steps[0].fields.find(f => f.key === 'emailSubject').defaultValue = emailTemplate.subject
        fieldDef.steps[0].fields.find(f => f.key === 'emailBodyRichText').defaultValue = emailTemplate.bodyRichText
        if (Array.isArray(emailTemplate.attachment) ? emailTemplate.attachment.length > 0 : emailTemplate.attachment) {
          const emailAttachmentField = fieldDef.steps[0].fields.find(f => f.key === 'emailAttachment')
          emailAttachmentField.defaultValue = emailTemplate.attachment
          emailAttachmentField.link = emailTemplate.attachmentLink
        }
      }
      const emailFromInboxField = fieldDef.steps[0].fields.find(f => f.key === 'emailFromInbox')
      const emailFromInbox = this.crmInboxes.find(inbox => inbox.id === emailFromInboxField.defaultValue) || {}
      if (formData.emailSignature === 'YES') {
        const emailBodyField = fieldDef.steps[0].fields.find(f => f.key === 'emailBodyRichText')
        const signatureRichText = Helper.renderPlainText(emailFromInbox.signatureRichText) ? emailFromInbox.signatureRichText : (this.crmUserSettingsEmailSignatureRichText || '')
        emailBodyField.defaultValue = emailBodyField.defaultValue ? Helper.mergeRichText(emailBodyField.defaultValue, signatureRichText) : signatureRichText
      }
      const templateValues = {
        'user.name': Helper.formatName(this.crmUser),
        'user.givenname': this.crmUser.givenname || '',
        'user.firstname': this.crmUser.givenname || '',
        'user.middlename': this.crmUser.middlename || '',
        'user.familyname': this.crmUser.familyname || '',
        'user.lastname': this.crmUser.familyname || '',
        'user.email': this.crmUser.email || '',
        'user.phone': this.crmUser.phone || '',
        'user.company': this.crmUser.company || '',
        'contact.name': Helper.formatName(this.crmUser),
        'contact.givenname': this.crmUser.givenname || '',
        'contact.firstname': this.crmUser.givenname || '',
        'contact.middlename': this.crmUser.middlename || '',
        'contact.familyname': this.crmUser.familyname || '',
        'contact.lastname': this.crmUser.familyname || '',
        'contact.friendlyname': this.crmUser.friendlyname || this.crmUser.givenname || '',
        'contact.email': this.crmUser.email || '',
        'contact.phone': this.crmUser.phone || '',
        'contact.company': this.crmUser.company || '',
        'contact.friendlycompanyname': this.crmUser.friendlycompanyname || this.crmUser.company || '',
        'sender.name': emailFromInbox.senderName || '',
        'sender.email': emailFromInbox.emailRaw || '',
      }
      const emailSubject = fieldDef.steps[0].fields.find(f => f.key === 'emailSubject')
      const emailBodyRichText = fieldDef.steps[0].fields.find(f => f.key === 'emailBodyRichText')
      emailSubject.defaultValue = Helper.replaceText(emailSubject.defaultValue || '', templateValues)
      emailBodyRichText.defaultValue = Helper.replaceRichText(emailBodyRichText.defaultValue, templateValues)
    } else if (formData.iType === 'WHATSAPP') {
      apiData.isSendWAMessage = true
      apiData.toNumber = this.crmUser && this.crmUser.phone
      apiData.subOrgId = this.crmUser && this.crmUser.subOrgId
      fieldDef = getSendWAMessageFieldDef({defaultType: defaultWAMessageType})
    } else if(formData.iType === 'EDIT_CONTACT') {
      fieldDef = JSON.parse(JSON.stringify(this.crmUserFieldDef))
      const editContactInteractionsFieldsCdpSettings = AppHelper.getSetting(this.orgId, 'CRM_CONTACT_EDIT_INTERACTION_FIELDS')
      const editContactInteractionsFields = editContactInteractionsFieldsCdpSettings && editContactInteractionsFieldsCdpSettings.split(',').map(item => item.trim()).filter(Boolean)
      if(editContactInteractionsFields && editContactInteractionsFields.length > 0) {
        fieldDef = Helper.getFilteredFieldDef(fieldDef, editContactInteractionsFields)
      }

    }

    if(this.isMobileView) {
      modifyFormDefForMobile(fieldDef)
    }

    if(formData && formData.isWAITypesOnly) {
      apiData.isWAITypesOnly = formData.isWAITypesOnly
    }

    if(formData.iType === 'EDIT_CONTACT') {
      const crmUserCp = this.state.crmUser && JSON.parse(JSON.stringify(this.state.crmUser))
      if(crmUserCp && !('linkedinConnectionStatus' in crmUserCp)) {
        crmUserCp.linkedinConnectionStatus = ''
      }
      this.setState({ fetchState: ApiHelper.State.READY, modalOpen: false }, () => {
        apiData.isAddStep2 = true
        this.openModalForEdit(crmUserCp, fieldDef)
      })
    } else if(formData.iType === 'WHATSAPP' && !apiData.toNumber) {
      this.setState({
        fetchState: ApiHelper.State.READY,
        modalOpen: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: Helper.getString('whatsAppUnavailableForContact')
      })
    } else {
      this.setState({ fetchState: ApiHelper.State.READY, modalOpen: false }, () => {
        apiData.isAddStep2 = true
        this.openModalForAdd(fieldDef, inReplyToCrmInteraction, isReplyAll, apiData, formData.iType)
      })
    }
  }

  customSubmitForSendWAContent(options) {
    const { formData, caller } = options
    const waContentId = formData && formData.waContentId
    const toNumber = formData && formData.toNumber
    const orgId = formData && formData.orgId
    const subOrgId = formData && formData.subOrgId
    const content = (this.waContents || []).find(c => c && c.id === waContentId)
    const isSessionActive = isWithin24HoursWindow(this.state.waMessages, this.state.agentWABA)

    if (!waContentId) {
      const errMsg = Helper.getString('provideWaMessage')
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg,
        success: false,
        message: errMsg,
        inProcess: false,
      })
      return
    }

    if (!content || !isWaContentSendable(content, { agentWABA: this.state.agentWABA, isSessionActive })) {
      const errMsg = Helper.getString(getOnlyWAInteractionsMessageKey(this.state.waMessages, this.state.agentWABA))
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg,
        success: false,
        message: errMsg,
        inProcess: false,
      })
      return
    }

    if (!orgId || !toNumber) {
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: Helper.getString('customerCareErr'),
        success: false,
        message: Helper.getString('customerCareErr'),
        inProcess: false,
      })
      return
    }

    this.pauseWaPolling()
    const templateValues = CRMHelper.addCrmContactTemplateValues(
      {},
      this.crmUser,
      'contact',
      {},
      true,
      this.crmUserFieldDef
    )
    const missingPlaceholders = CRMHelper.getMissingContactPlaceholders(
      CRMHelper.collectWaContentVariableTexts(content),
      templateValues
    )
    if (missingPlaceholders.length > 0) {
      const errMsg = Helper.getString('waSendMissingContactValues')
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg,
        success: false,
        message: errMsg,
        inProcess: false,
      })
      return
    }
    const jsonBody = {
      orgId,
      waContentId,
      toNumber,
      templateValues,
      senderUserId: this.staffUserId,
    }
    if (String(content.iType || '').toUpperCase() === WA_CONTENT_TYPE_SESSION && this.state.agentWABA) {
      jsonBody.fromNumber = this.state.agentWABA
    }
    if (subOrgId) {
      jsonBody.subOrgId = subOrgId
    }
    ApiHelper.callAwait({
      ms: this.ms,
      paths: ['miscs', 'send-whatsapp'],
      method: 'POST',
      jsonBody,
      queryParams: { orgId: this.orgId },
    }).then((response) => {
      if (response && Array.isArray(response.items) && response.items[0] && response.items[0].error) {
        const errMsg = (response.items[0].error.payload && response.items[0].error.payload.message)
          ? response.items[0].error.payload.message
          : Helper.getString('customerCareErr')
        this.resumeWaPolling()
        caller.setState({
          fetchState: ApiHelper.State.ERROR,
          errMsg,
          success: false,
          message: errMsg,
          inProcess: false,
        })
        return
      }
      this.resumeWaPolling()
      this.setState({
        fetchState: ApiHelper.State.READY,
        modalOpen: false,
      }, () => {
        this.fetchWaMessages({ isPolling: false, includeAgentWABA: true })
      })
    }).catch((err) => {
      const errorMessage = Helper.getErrorMsg(err)
      this.resumeWaPolling()
      this.setState({
        fetchState: ApiHelper.State.ERROR,
        modalOpen: false,
        errMsg: errorMessage,
      })
    })
  }

  sendTextWAMessage(options) {
    this.pauseWaPolling()
    this.setState({
      fetchState: ApiHelper.State.LOADING,
      inSendWAMsgProcess: true,
      loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
    })

    const orgId = this.orgId
    const type = 'text'
    const subOrgId = this.crmUser && this.crmUser.subOrgId
    const messageParam = {
      toNumber: this.crmUser && this.crmUser.phone,
      text: (options && options.waTextMsg) ? options.waTextMsg  : ''
    }
    if(orgId && type && messageParam && messageParam.toNumber && messageParam.text) {
      ApiHelper.callAwait({
        ms: this.ms,
        paths: ['miscs', 'send-whatsapp'],
        method: 'POST',
        jsonBody: { orgId, subOrgId, type, messageParams: [messageParam], senderUserId: this.staffUserId},
        queryParams: {orgId: this.orgId}}
      ).then(response => {
        if(response && Array.isArray(response.items) && response.items[0] && response.items[0].error) {
          const errMsg = (response.items[0].error.payload && response.items[0].error.payload.message) ?  response.items[0].error.payload.message : Helper.getString('customerCareErr')
          this.resumeWaPolling()
          this.setState({
            fetchState: ApiHelper.State.READY,
            inSendWAMsgProcess: false,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
            showToast: true,
            isErrorToast: true,
            toastMsg: errMsg,
          })
        } else {
          this.resumeWaPolling()
          this.setState({
            fetchState: ApiHelper.State.READY,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          }, () => {
            this.fetchWaMessages({ isPolling: true, includeAgentWABA: true }, () => {
              this.setState({ inSendWAMsgProcess: false, waTextMsg: '' })
            })
          })
        }
      }).catch(err => {
        const errorMessage = Helper.getErrorMsg(err)
        this.resumeWaPolling()
        this.setState({
          fetchState: ApiHelper.State.READY,
          inSendWAMsgProcess: false,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          showToast: true,
          isErrorToast: true,
          toastMsg: errorMessage,
        })
      })
    } else {
      this.resumeWaPolling()
      this.setState({
        fetchState: ApiHelper.State.READY,
        inSendWAMsgProcess: false,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
        showToast: true,
        isErrorToast: true,
        toastMsg: 'Unable to send message.',
      })
    }
  }

  customSubmitForSendWAMessage(options) {
    const { formData, caller } = options
    delete formData.isAddStep1
    delete formData.isSendWAMessage
    const waMessages = this.state.waMessages && JSON.parse(JSON.stringify(this.state.waMessages))

    if(Array.isArray(waMessages) && waMessages.length > 0) {
      const isWithin24Hours = isWithin24HoursWindow(waMessages,this.state.agentWABA)

      if(isWithin24Hours) {
        if(formData) {
          const orgId = formData.orgId
          const subOrgId = formData.subOrgId
          const toNumber = formData.toNumber
          let messageParam = null
          const type = (formData.type === 'TEXT') ? 'text' : (formData.type === 'IMAGE') ? 'image' : (formData.type === 'DOCUMENT') ? 'document' : null
          if(type === 'text') {
            messageParam = {
              toNumber: toNumber,
              text: formData.messageBody || ''
            }
          } else if(type === 'image') {
            messageParam = {
              toNumber: toNumber,
              link: formData.image,
              caption: formData.caption || ''
            }
          } else if(type === 'document') {
            messageParam = {
              toNumber: toNumber,
              link: formData.attachment,
              filename: extractFilename(formData.attachment) || '',
              caption: formData.caption || ''
            }
          }

          if(orgId && toNumber && type && messageParam) {
            this.pauseWaPolling()
            ApiHelper.callAwait({
              ms: this.ms,
              paths: ['miscs', 'send-whatsapp'],
              method: 'POST',
              jsonBody: { orgId, subOrgId, type, messageParams: [messageParam], senderUserId: this.staffUserId},
              queryParams: {orgId: this.orgId}}
            ).then(response => {
              if(response && Array.isArray(response.items) && response.items[0] && response.items[0].error) {
                const errMsg = (response.items[0].error.payload && response.items[0].error.payload.message) ?  response.items[0].error.payload.message : Helper.getString('customerCareErr')
                this.resumeWaPolling()
                caller.setState({
                  fetchState: ApiHelper.State.ERROR,
                  errMsg: errMsg,
                  success: false,
                  message: errMsg,
                  inProcess: false
                })
              } else {
                this.resumeWaPolling()
                this.setState({
                  fetchState: ApiHelper.State.READY,
                  modalOpen: false
                }, () => {
                  this.fetchWaMessages({ isPolling: false, includeAgentWABA: true })
                })
              }
            }).catch(err => {
              const errorMessage = Helper.getErrorMsg(err)
              this.resumeWaPolling()
              this.setState({
                fetchState: ApiHelper.State.ERROR,
                modalOpen: false,
                errMsg: errorMessage,
              })
            })
          } else {
            caller.setState({
              fetchState: ApiHelper.State.ERROR,
              errMsg: Helper.getString('customerCareErr'),
              success: false,
              message: Helper.getString('customerCareErr'),
              inProcess: false
            })
          }
        } else {
          caller.setState({
            fetchState: ApiHelper.State.ERROR,
            errMsg: Helper.getString('customerCareErr'),
            success: false,
            message: Helper.getString('customerCareErr'),
            inProcess: false
          })
        }
      } else {
        const onlyWAInteractionsMessage = Helper.getString(getOnlyWAInteractionsMessageKey(waMessages, this.state.agentWABA))
        caller.setState({
          fetchState: ApiHelper.State.ERROR,
          errMsg: onlyWAInteractionsMessage,
          success: false,
          message: onlyWAInteractionsMessage,
          inProcess: false
        })
      }
    }
  }

  async customSubmit(options) {
    const { formType, formData, caller } = options
    if (formData.isAddStep1) { return this.customSubmitForAddStep1(options) }
    if(formData.isSendWAMessage) { return this.customSubmitForSendWAMessage(options) }
    if(formData.isSendWAContent) { return this.customSubmitForSendWAContent(options) }
    if ('seqEnrolmentNextStepIndex' in formData) { return UiHelper.customSubmitForSequences.call(this, options) }
    if (formData.enrichFields) { return UiHelper.customSubmitForEnrich.call(this, options) }

    const apiData = JSON.parse(JSON.stringify(formData))
    if(apiData.isAddStep2) {
      delete apiData.isAddStep2

      if (apiData.iType in this.workflows) {
        apiData._isWorkflow = 'YES'
        apiData.payload = { _workflowData: {} }
        this.workflows[apiData.iType].fieldDef.steps[0].fields.forEach(f => {
          apiData.payload._workflowData[f.key] = apiData[f.key]
          delete apiData[f.key]
        })
      }

      const addresseeFieldNames = ['emailTo', 'emailCC', 'emailBCC']
      addresseeFieldNames.forEach(fieldName => {
        if (apiData[fieldName] && apiData[fieldName].length > 0) {
          const field = this.fieldDef.steps[0].fields.find(f => f.key === fieldName)
          apiData[fieldName].forEach((email,index)=>{
            const value = field.options.some(option => option.key === email) ? field.options.find((option)=>option.key === email).label : email
            apiData[fieldName][index] = value
          })
        }
      })
    }
    return caller.submit.call(caller, apiData, true);
  }

  processEmailInteractionHtml(current, summary) {
    const html = Helper.parseStringToHtml(summary)
    const moreContainer1 = html.querySelector('blockquote')
    if(moreContainer1) {
      moreContainer1.style.display = 'none'
      moreContainer1.id = `moreContainer1_${current.__index}`

      const moreContainer2 = moreContainer1.previousElementSibling
      if(moreContainer2) {
        moreContainer2.style.display = 'none'
        moreContainer2.id = `moreContainer2_${current.__index}`
      }
    }
    const newSummary = Helper.serializeHtmlToString(html.body)
    return newSummary
  }

  handleEmailMoreBtnClick(current) {
    const moreContainer1 = document.querySelector(`#moreContainer1_${current.__index}`)
    const moreContainer2 = document.querySelector(`#moreContainer2_${current.__index}`)
    if(moreContainer1) {
      if (moreContainer1.style.display === 'none') {
        moreContainer1.style.display = 'block';
      } else {
        moreContainer1.style.display = 'none';
      }
    }
    if(moreContainer2) {
      if (moreContainer2.style.display === 'none') {
        moreContainer2.style.display = 'block';
      } else {
        moreContainer2.style.display = 'none';
      }
    }
  }
  openModalForEditPaymentConfirmation(current, pcTransactionAmount) {
    this.apiData = {
      orgId: this.orgId,
      interactionId: current.id,
      ...AppHelper.getTeamBodyParams(this.orgId, '_staffSubOrgId'),
      _isEditPaymentConfirmation: true,
    }
    this.formDefinition = {
      title: 'Edit transaction',
      formType: 'add',
      ctaButtonText: 'Save',
      steps: [{
        fields: [
          {
            key: 'transactionAmount',
            type: 'edit_text',
            label: 'Transaction Amount',
            tip: 'Enter the transaction amount',
            v_required: { value: true, err: 'Provide transaction amount' },
            v_float: {value: true, err: 'Transaction amount must be a number'},
            placement: 'left',
            defaultValue: pcTransactionAmount,
          }
        ]
      }],
    }
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['miscs', 'update-payment-transaction'], queryParams: { orgId: this.orgId, ...(AppHelper.isTeamsUnifiedView(this.orgId) ? AppHelper.getTeamApiParams(this.orgId) : {}) } }),
      modalOpen: true
    });
  }
}

const statsSkeletonPulse = {
  '0%': { opacity: 0.45 },
  '50%': { opacity: 1 },
  '100%': { opacity: 0.45 },
}

const Styles = StyleSheet.create({
  waicon: {
    padding: 0,
    paddingBottom: 5,
    height: '23.5px',
    ':hover': {
      background: 'none'
    },
    color: AppTheme.primaryColor,
  },
  line1: {
    fontSize: 16,
  },
  line2: {
    fontSize: 12,
    color: 'grey'
  },
  interactionInfoMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  interactionInfoLine: {
    fontSize: 12,
    color: 'grey',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  },
  interactionInfoLink: {
    color: 'inherit',
    textDecoration: 'underline',
  },
  interactionInfoTooltipRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  statusBadge: {
    backgroundColor: AppTheme.primaryBg,
    padding: 5,
    borderRadius: 10,
    wordBreak: 'keep-all',
    display: 'block',
    textAlign: 'center',
    maxWidth: 100,
  },
  statusBadgeContact: {
    backgroundColor: '#3BDE86',
    color: '#222222'
  },
  emailMoreBtn: {
    backgroundColor: AppTheme.primaryBg,
    fontSize: 8,
    borderRadius: 10,
    wordBreak: 'keep-all',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    cursor: 'pointer',
  },
  summaryContainer: {
    wordBreak: 'break-word'
  },
  broadcastEmailPreviewBox: {
    marginTop: 8,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    backgroundColor: '#fafafa',
    maxHeight: 140,
    overflow: 'hidden',
    cursor: 'pointer',
    position: 'relative',
  },
  broadcastEmailPreviewInner: {
    pointerEvents: 'none',
  },
  broadcastEmailPreviewDialogContent: {
    padding: 20,
    wordBreak: 'break-word',
  },
  webhookResponseDialogContent: {
    margin: 0,
    padding: 20,
    fontSize: 12,
    lineHeight: '18px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  eventStreamBlock: {
    marginTop: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  eventStreamRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  eventStreamTitleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '6px 10px',
  },
  eventStreamEventName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#777777',
    wordBreak: 'break-word',
  },
  eventStreamParenWrap: {
    fontWeight: 'normal',
  },
  eventStreamLink: {
    color: AppTheme.primaryColor,
    textDecoration: 'underline',
    wordBreak: 'break-all',
    fontWeight: 'normal',
  },
  cellWidthSmall: {
    width: '10%',
    padding: '8px 5px',
  },
  cellWidthNormal: {
    width: '20%',
    padding: '8px 5px',
  },
  cellWidthWide: {
    width: '50%',
    padding: '8px 5px',
  },
  fullWidthWide: {
    width: '100%',
    padding: '8px 5px',
    '@media (max-width: 767px)': {
      fontSize: 10,
      padding: '5px 2px',
    },
  },
  containerNarrow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 10,
    maxWidth: AppTheme.viewPort,
    paddingBottom: 30,
    width: '100%',
    '@media (max-width: 767px)': {//TODO: next?
      //width: window.innerWidth - 20 //TODO: fix it, in product screen whitespace nowrap creating issue
    },
  },
  containerNarrowAboveTable: {
    paddingBottom: 0,
  },
  fieldActionsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 10,
    '@media (max-width: 767px)': {
      gap: 5
    },
  },
  connectButton: {
    color: AppTheme.buttonTextColor,
    backgroundColor: AppTheme.buttonBg,
    fontWeight: 600,
    padding: 7,
    fontSize: 14,
    textTransform: 'none',
    minWidth: 80,
    height: 25,
    marginBottom: 6,
    borderRadius: 6,
    '@media (max-width: 767px)': {
      minWidth: 80,
    }
  },
  connectButtonText: {
    color: AppTheme.buttonTextColor,
    fontWeight: 600,
    fontSize: 14,
    margin: 0,
    textTransform: 'none',
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  actionsSelect: {
    minWidth: 140,
    paddingLeft: 10,
    paddingRight: 5,
    border: '1px solid #E5E5E5',
    textDecoration: 'none',
    borderRadius: 16,
    fontSize: 14,
    height: 25,
    marginBottom: 6,
    color: '#222222',
    '@media (max-width: 767px)': {
      minWidth: 110,
      fontSize: 12,
    },
  },
  actionsSelection: {
    color: '#222222',
    ':focus': {
      backgroundColor: 'inherit !important',
    }
  },
  actionsSelectMenu: {
    color: '#222222',
    marginTop: 3,
  },
  actionCircularProgress: {
    paddingBottom: 2,
    paddingLeft: 4
  },
  link: {
    textDecoration: 'none',
  },
  numericStatItemRow: {
    '@media (max-width: 1024px)': {
      width: '98%',
      alignSelf: 'center',
      paddingTop: '8px',
      paddingBottom: '8px',
      height: '100%',
    },
    paddingTop: '8px',
    paddingBottom: '8px',
    width: '100%',
  },
  numericStatContainerRow: {
    height: '100%',
    maxHeight: '100%',
    maxWidth: AppTheme.viewPort,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '2px 0px 8px 0px',
    margin: 0,
    gap: 5,
    '@media (max-width: 767px)': {
      justifyContent: 'space-between',
      padding: '2px 0px',
      gap: 0
    },
  },
  statsCardContainer: {
    '@media (max-width: 767px)': {
      maxWidth: 'unset',
    },
    maxWidth: '240px',
    flex: 1,
  },
  statsCardContainerFull: {
    maxWidth: 'unset',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 767px)': {
      alignItems: 'center',
    },
  },
  statsSectionCollapse: {
    width: '100%',
    alignSelf: 'stretch',
  },
  statsSkeletonValue: {
    display: 'inline-block',
    width: '45%',
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    animationName: statsSkeletonPulse,
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
  },
  statsSkeletonLabel: {
    display: 'inline-block',
    width: '70%',
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    animationName: statsSkeletonPulse,
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
  },
  statsCard: {
    width: '100%',
  },
  showUpdateInteractions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  customModalDescriptionStyles: {
    color: 'green'
  },
  waStatusText: {
    color: '#aaaaaa',
    fontWeight: 'bold',
    fontSize: 12,
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  },
  interactionsMobileStatusContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 5,
    paddingTop: 4,
  },
  interactionsMobileActionsContainer:  {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 5,
  },
  waStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: 5
  },
  waChatOverlayHost: {
    position: 'relative',
  },
  chatRow: {
    display: 'flex',
    marginBottom: 8
  },
  chatRowIncoming: {
    justifyContent: 'flex-start'
  },

  chatRowOutgoing: {
    justifyContent: 'flex-end'
  },
  chatBubble: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 5,
    maxWidth: '80%',
    minWidth: '30%',
    padding: '8px 10px',
    borderRadius: 8,
    position: 'relative',
    backgroundColor: '#f0f2f5',
    boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
  },
  chatBubbleWhatsappPreview: {
    width: 360,
    maxWidth: '72%',
    minWidth: 320,
    '@media (max-width: 600px)': {
      width: 'auto',
      maxWidth: '92%',
      minWidth: 0,
    },
  },
  chatBubbleInteraction: {
    width: '100%',
    maxWidth: '100%',
  },
  chatBubbleTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#777777',
  },
  chatBubbleMsgPart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '100%',
    gap: 5
  },
  bubbleIncoming: {
    backgroundColor: '#f0f2f5',
    borderTopLeftRadius: 0
  },
  bubbleOutgoing: {
    backgroundColor: '#dcf8c6',
    borderTopRightRadius: 0
  },
  senderName: {
    fontSize: 12,
    fontWeight: 600,
    color: 'grey',
    marginBottom: 4
  },
  senderNameContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 8
  },
  messageText: {
    fontSize: 14,
    lineHeight: '18px',
    color: '#222222',
    wordBreak: 'break-word'
  },
  interactionInfoGroup: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  tooltip: {
    zIndex: 9999999999,
    maxWidth: 250,
    fontSize: 12,
    textAlign: 'left'
  },
  interactionInfoContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: 5
  },
  dialogHeadercontainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 25
  },
  dialogHeaderImageContainer: {
    width: 48,
    height: 48,
    backgroundColor: AppTheme.secondaryBg,
    borderRadius: '50%',
    display: 'flex',
    cursor: 'pointer',
  },
  dialogHeaderIconLabelContainer: {
    minWidth:0,
    flex:'1 1 0',
    maxWidth: 48,
    color: AppTheme.primaryColor,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  dialogHeaderIconLabelText: {
    margin: 0,
    fontSize: 12,
    textTransform: 'capitalize',
  },
  dialogHeaderButton: {
    minWidth: 160,
    marginLeft: 10
  },
  onlyWAInteractionsDescriptionStyles: {
    color: 'green',
    width: '100%',
    whiteSpace: 'pre-wrap'
  },
  waStatusGroup: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  waStatusGroupHidden: {
    display: 'none'
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '260px'
  },
  // mediaThumbnail: {
  //   position: 'relative',
  //   width: 220,
  //   aspectRatio: '16 / 9',
  //   borderRadius: 6,
  //   backgroundColor: '#e4e6eb',
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   flexDirection: 'column'
  // },
  progress: {
    color: '#222222'
  },
  playIcon: {
    fontSize: '48px',
    color: '#222222',
    marginBottom: '4px'
  },
  imageIcon: {
    fontSize: '48px',
    marginBottom: '4px'
  },
  captionText: {
    marginTop: '6px',
    fontSize: '14px',
    lineHeight: '18px',
    color: '#222222'
  },
  mediaLink: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#222222',
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer'
  },
  mediaBubble: {
    width: 220,
    aspectRatio: '16 / 9',
    borderRadius: 6,
    backgroundColor: '#e4e6eb',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    marginBottom: 6,
    marginTop: 5,
    fontWeight: 500,
    gap: 5
  },
  mediaAudioBubble: {
    padding: 2,
    aspectRatio: 'unset'
  },
  mediaPreview: {
    width: '100%',
    height: 'auto',
    maxHeight: '50vh',
    objectFit: 'contain',
    display: 'block'
  },
  mediaAudioPreview: {
    width: '100%',
    height: 40,
  },
  sendWAcontainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingBottom: 10,
    borderBottom: '2px solid #E5E5E5',
    '@media (max-width: 1024px)': {
      gap: 5,
    },
  },
  formControl: {
    margin: 0,
    flexGrow: 1,
    transition: 'flex 220ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  formControlExpanded: {
    flexGrow: 1,
  },
  sendWAInput: {
    minWidth: 150,
    padding: 10,
    paddingLeft: 22,
    paddingRight: 56,
    border: '1px solid #E5E5E5',
    borderRadius: 16,
    fontSize: 16,
    color: '#222222',
    minHeight: 54,
    position: 'relative',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    '& textarea': {
      padding: 0,
      resize: 'none',
    }
  },
  sendWAEndAdornment: {
    position: 'absolute',
    right: 0,
    bottom: 10,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonSendWALoading: {
    color: '#FFFFFF',
    boxShadow: 'none',
    border: '1px solid #E5E5E5',
    borderRadius: '0 16px 16px 0',
    backgroundColor: AppTheme.primaryColor,
    fontSize: 28,
    fontWeight: 500,
    minWidth: 'unset',
    lineHeight: 'unset',
    textTransform: 'unset',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonSendWAEnabled: {
    color: '#FFFFFF',
    boxShadow: 'none',
    border: '1px solid #E5E5E5',
    borderRadius: '0 16px 16px 0',
    backgroundColor: AppTheme.primaryColor,
    width: '100%',
    fontSize: 16,
    fontWeight: 500,
    padding: 17,
    minWidth: 'unset',
    lineHeight: 'unset',
    textTransform: 'unset',
    '@media (max-width: 1024px)': {
      padding: 15,
    },
  },
  buttonSendWADisabled: {
    color: '#222222',
    backgroundColor: AppTheme.secondaryColor,
  },
  buttonTextEnabled: {
    color: '#FFFFFF',
  },
  buttonTextDisabled: {
    color: '#808080'
  },
  mediaButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#222222',
    boxShadow: 'none',
    border: '1px solid #E5E5E5',
    borderRadius: 16,
    backgroundColor: AppTheme.primaryColor,
    fontSize: 16,
    fontWeight: 500,
    height: 54,
    width: 54,
    minWidth: 'unset',
    textTransform: 'unset',
    '@media (max-width: 1024px)': {
      width: 44,
    },
  },
  mediaButtonLoading: {
    padding: 0,
  },
  mediaButtonLoadingProgress: {
    color: '#FFFFFF',
  },
  mediaButtonMobile:{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#222222',
    boxShadow: 'none',
    border: '1px solid #E5E5E5',
    borderRadius: 16,
    backgroundColor: AppTheme.primaryColor,
    fontSize: 16,
    fontWeight: 500,
    height: 54,
    width: '100%',
    minWidth: 'unset',
    textTransform: 'unset',
  },
  mediaButtonTextMobile:{
    fontSize: 10,
    '@media (max-width: 380px)': {
      fontSize: 8,
    },
  },
  iconContainerStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    cursor: 'pointer',
    margin: 0,
    border: 'unset',
    '@media (max-width: 1024px)': {
      padding: 1,
    },
  },
  selectedIconContainerStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    cursor: 'pointer',
    margin: 0,
    border: 'unset',
    '@media (max-width: 1024px)': {
      padding: 1,
    },
  },
  sendWAIconContainerStyle: {
    color: '#FFFFFF'
  },
  sendWAIconContainerStyleDisabled: {
    color: AppTheme.primaryColor,
  },
  iconStyle: {
    fontSize: 28,
    '@media (max-width: 1024px)': {
      fontSize: 22,
    },
  },
  onlyWAInteractionsWarning: {
    color: 'green',
    fontSize: 16,
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    '@media (max-width: 1024px)': {
      fontSize: 14,
    },
  },
  whatsAppUnavailableWarning:{
    color: 'green',
    fontSize: 16,
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    textAlign: 'center',
    '@media (max-width: 1024px)': {
      fontSize: 14,
    },
  },
  mediaActionsWrapper: {
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    opacity: 1,
    transform: 'translateX(0)',
    transition: `
    width 220ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 160ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 220ms cubic-bezier(0.4, 0, 0.2, 1)
  `,
  },
  mediaActionsHidden: {
    width: 0,
    opacity: 0,
    transform: 'translateX(12px)',
    pointerEvents: 'none',
  },
  actionDialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 200,
    marginBottom:12,
  },
  actionDialogButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    margin:'2px',
    fontSize: 14,
    fontFamily:'inherit',
    backgroundColor: 'rgb(249,249,249) !important',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    color: AppTheme.primaryColor,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      borderColor: AppTheme.primaryColor,
    },
    ':active': {
      transform: 'scale(0.98)',
    }
  },
  actionsDialogText:{
    color: AppTheme.primaryColor,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
    textAlign: 'left',
    margin: 0,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    lineHeight: 1.4,
  },
  mediaLabel: {
    display: 'inline-block',
    maxWidth: 200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  mediaLabelOpen: {
    fontSize: 13,
    fontWeight: 500,
    color: AppTheme.primaryColor,
    backgroundColor: 'transparent',
    padding: '4px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    userSelect: 'none',
  },
  iconLabelLink: {
    textDecoration: 'none',
  },
  wabaPhoneIcon:{
    color:'#aca8a8',
    cursor: 'pointer',
  }
})